import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import MetaTags from "react-meta-tags";
import { withRouter } from "react-router-dom";
import filterFactory, { textFilter } from "react-bootstrap-table2-filter";
import axios from "axios";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
} from "reactstrap";
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
} from "react-bootstrap-table2-paginator";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";
import Breadcrumbs from "components/Common/Breadcrumb";
import jsPDF from "jspdf";
import {
  sampleReport,
  postSampleReport,
  updateSampleReport,
} from "store/databaseofunits/actions";
import "assets/scss/table.scss";

// ─── Helpers ────────────────────────────────────────────────────────────────

const SCHEME_MAP = {
  "Coagulation (641a-CO)": "COA (641a-CO)",
  "Immunoassay (121a-IA)": "IA (121a-IA)",
  "HCV Molecular (603-HCV)": "HCV (603-HCV)",
  "HBV Molecular (603-HBV)": "HBV (603-HBV)",
  "Syphlis (881-SY)": "SY (881-SY)",
  "Serology (456-SS)": "SS (456-SS)",
  "Microbiology (65-MIC)": "MIC (65-MIC)",
  "Test micro 1": "MIC1",
  "Haemoglobin (555-HB)": "Hb (555-HB)",
  "Hematology (444)": "HEM (444)",
  "General Chemistry (571a-GC)": "CHE-a (571a-GC)",
  "General Chemistry (571b-GC)": "CHE-b (571b-GC)",
  "Immunoassay (121b-IA)": "IA-b (121b-IA)",
  Urinalysis: "URI",
  "Test Hematology": "TST",
};

// Sentinel value used by the City and Lab Type dropdowns for the
// "All GCC Labs" shortcut option. Selecting it filters the table down
// to labs whose type is GCC, across every city.
const GCC_FILTER_VALUE = "__ALL_GCC_LABS__";
const GCC_FILTER_LABEL = "All GCC Labs";

// Channel used by the Delivery Challan tab to tell this page that a DC
// was created / printed, so the checkbox and row color update instantly.
const DC_CHANNEL = "dc_status_channel";

const API_BASE = "https://externalqcapi.com/api";

// ✅ Filters are remembered in sessionStorage so that going to the
// Delivery Challan page and coming back (or refreshing) keeps whatever
// filters were selected on this page.
const FILTER_STORAGE_KEY = "sample_dispatch_report_filters";

const PERSISTED_FILTER_KEYS = [
  "dispatchTypeFilter",
  "selectedSchemeFilter",
  "selectedCycleFilter",
  "selectedRoundFilter",
  "selectedRoundPeriodFilter",
  "selectedDistrictFilter",
  "selectedCityFilter",
  "selectedTypeFilter",
  "selectedStatusFilter",
];

const loadSavedFilters = () => {
  try {
    const raw = sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw) || {};
    const clean = {};

    PERSISTED_FILTER_KEYS.forEach(k => {
      if (parsed[k] !== undefined && parsed[k] !== null) clean[k] = parsed[k];
    });

    // Multi-select filters must always be non-empty arrays.
    [
      "selectedDistrictFilter",
      "selectedCityFilter",
      "selectedTypeFilter",
    ].forEach(k => {
      if (clean[k] !== undefined) {
        if (!Array.isArray(clean[k]) || clean[k].length === 0) {
          clean[k] = ["All"];
        }
      }
    });

    return clean;
  } catch (e) {
    return {};
  }
};

const saveFilters = state => {
  try {
    const toSave = {};
    PERSISTED_FILTER_KEYS.forEach(k => {
      toSave[k] = state[k];
    });
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(toSave));
  } catch (e) {
    // storage unavailable — filters just won't persist
  }
};

const getAbbr = name => SCHEME_MAP[name] || name;

const formatDate = d => {
  if (!d) return "-";
  const dt = new Date(d);
  if (isNaN(dt)) return "-";
  return `${String(dt.getDate()).padStart(2, "0")}-${dt.toLocaleString(
    "en-GB",
    { month: "short" }
  )}-${String(dt.getFullYear()).slice(-2)}`;
};

const normalize = s =>
  typeof s === "string"
    ? s.toLowerCase().trim()
    : String(s ?? "")
        .toLowerCase()
        .trim();

// Coerces API values for dc_created/dc_printing to real booleans. Plain
// `!!value` treats the STRING "false" as truthy, which silently broke the
// red/green row coloring whenever the API sent booleans as strings.
const toBool = v => v === true || v === "true" || v === 1 || v === "1";

// Opens a URL in a new TAB (window.open with a features string can make
// some browsers open a separate popup window instead).
const openInNewTab = href => {
  const a = document.createElement("a");
  a.href = href;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const filterStyle = {
  width: "100%",
  height: "32px",
  marginTop: "6px",
  fontSize: "12px",
  padding: "4px 6px",
  boxSizing: "border-box",
  textAlign: "center",
};

const tf = () => textFilter({ style: filterStyle });

// ✅ A round only counts as "open for dispatch" here when its status is
// Open AND it's flagged open_for "Both" or "Customer Service". A round
// that is Open but open_for "Participant" only is meant for the
// participant portal, not this Customer Service dispatch report, so it's
// treated as if it weren't open at all anywhere in this component.
const isRoundOpenForDispatch = round => {
  if (round?.status?.toLowerCase() !== "open") return false;
  const of = normalize(round?.open_for || "");
  return of === "both" || of === "customer_service";
};

const roundMatchesDispatchType = (round, dispatchType) => {
  if (dispatchType === "New") return isRoundOpenForDispatch(round);
  const st = round?.status?.toLowerCase();
  if (dispatchType === "Previous")
    return ["closed", "report available"].includes(st);
  return false;
};

// ─── Component ──────────────────────────────────────────────────────────────

const INIT_FORM = {
  dispatchDate: "",
  trackingId: "",
  comments: "",
};

const INIT_STATE = {
  structuredData: [],
  editValues: {},
  schemes: [],
  allSchemes: [],
  modalOpen: false,
  modalMode: "add",
  selectedRowData: null,
  modalSchemes: [],
  selectedSchemeFilter: "All",
  selectedCycleFilter: "All",
  // District filter is multi-select: an array of chosen values.
  // "All" is a special sentinel meaning "no district restriction".
  // Selecting a District narrows down the options offered in the City
  // filter (see getCityOptionsSource) and resets the City selection.
  selectedDistrictFilter: ["All"],
  districtDropdownOpen: false,
  districtSearchTerm: "",
  // City filter is multi-select: an array of chosen values.
  // "All" is a special sentinel meaning "no city restriction".
  selectedCityFilter: ["All"],
  cityDropdownOpen: false,
  // Lab Type filter is also multi-select, same shape/behavior as City.
  selectedTypeFilter: ["All"],
  citySearchTerm: "",
  typeDropdownOpen: false,
  selectedStatusFilter: "All",
  selectedRoundFilter: "All",
  // ✅ Round Period filter: Previous / Current / Next. Only meaningful
  // for "New Dispatches" — narrows participants down to those with an
  // open round whose round_period matches the selection.
  selectedRoundPeriodFilter: "All",
  cyclesForSelectedScheme: [],
  roundsForSelectedScheme: [],
  selectedRoundParticipants: [],
  dispatchTypeFilter: "New",
  checkedRounds: {},
  formData: INIT_FORM,
  formErrors: {},
  toastMessage: "",
  showDownloadConfirm: false,
  // 🔔 DC pending bell
  bellOpen: false,
  bellSearch: "",
  // When set, the table shows only this participant (clicked from the bell).
  focusedParticipantId: null,
  // 🚚 Every saved Delivery Challan line (participant + scheme + rounds).
  // Used to find which lab / scheme / round still has NO challan.
  dcLines: [],
  dcLinesLoaded: false,
};

class SchemeDetailsReport extends Component {
  // Used to scroll down to the table when a lab is picked from the bell.
  tableTopRef = React.createRef();

  constructor(props) {
    super(props);
    const authUser = localStorage.getItem("authUser");
    const userId = authUser ? JSON.parse(authUser).user_id : "";
    this.state = {
      ...INIT_STATE,
      // ✅ Restore the filters the user had before leaving this page.
      ...loadSavedFilters(),
      user_id: userId,
    };
  }

  componentDidMount() {
    const { user_id } = this.state;
    const { SampleReport } = this.props;

    // ✅ If redux already has the report (e.g. coming back from the
    // Delivery Challan page), render it right away with the restored
    // filters instead of waiting for the refetch below.
    if (Array.isArray(SampleReport) && SampleReport.length) {
      this.applyReport(SampleReport);
    }

    if (user_id) {
      this.props.onGetSampleReport(user_id);
    } else {
      console.error("❌ user_id is missing. Sample API NOT called.");
    }

    // 🚚 Which schemes / rounds already have a Delivery Challan.
    this.fetchDcLines();

    // ✅ The Delivery Challan opens in its own tab (its own Redux store),
    // so it tells this tab when a DC is created / printed.
    try {
      this.dcChannel = new BroadcastChannel(DC_CHANNEL);
      this.dcChannel.onmessage = e => {
        const { participantId, field, value } = e.data || {};
        if (!participantId || !field) return;

        // Instant checkbox + row color update...
        this.setState(prev => ({
          structuredData: prev.structuredData.map(r =>
            r.id === String(participantId)
              ? { ...r, [field]: toBool(value) }
              : r
          ),
        }));

        // ...then sync with the server copy.
        if (this.state.user_id)
          this.props.onGetSampleReport(this.state.user_id);
        this.fetchDcLines();
      };
    } catch (err) {
      // BroadcastChannel unsupported - the focus refetch below still covers it
    }

    window.addEventListener("focus", this.handleWindowFocus);
  }

  componentDidUpdate(prevProps, prevState) {
    const { SampleReport } = this.props;

    if (
      prevProps.SampleReport !== SampleReport &&
      Array.isArray(SampleReport)
    ) {
      this.applyReport(SampleReport);
    }

    // ✅ Persist filters whenever any of them changes.
    if (PERSISTED_FILTER_KEYS.some(k => prevState[k] !== this.state[k])) {
      saveFilters(this.state);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("focus", this.handleWindowFocus);
    if (this.dcChannel) this.dcChannel.close();
  }

  // Fallback: whenever the user switches back to this tab, refresh the data.
  handleWindowFocus = () => {
    if (this.state.user_id) this.props.onGetSampleReport(this.state.user_id);
    this.fetchDcLines();
  };

  // ─── Delivery Challan coverage (per lab + scheme + round) ──────────────

  // Loads every saved DC line (lightweight ?summary=1 mode of the API).
  fetchDcLines = async () => {
    const authUser = localStorage.getItem("authUser");
    const token = authUser ? JSON.parse(authUser).token : "";

    if (!token) {
      console.error(
        "❌ No token in localStorage 'authUser' - DC lines not loaded."
      );
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/database-admin/delivery-challans/?summary=1`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        console.error("❌ DC lines failed:", response.status, data);
        throw new Error(data.detail || data.error || "Could not load DCs");
      }

      // Accept either shape, in case the server returns serializer rows
      const lines = (Array.isArray(data) ? data : []).map(l => ({
        ...l,
        participant_id:
          l.participant_id ??
          (typeof l.participant === "object"
            ? l.participant?.id
            : l.participant),
      }));

      this.setState({ dcLines: lines, dcLinesLoaded: true });
    } catch (err) {
      console.error("❌ Failed to load delivery challan lines:", err);
    }
  };

  // Set of "participantId|scheme|round" that already have a DC row.
  buildDcCoverage = () => {
    const covered = new Set();

    (this.state.dcLines || []).forEach(l => {
      String(l.rounds || "")
        .split(",")
        .map(r => normalize(r))
        .filter(Boolean)
        .forEach(r => {
          covered.add(`${l.participant_id}|${normalize(l.scheme_name)}|${r}`);
        });
    });

    return covered;
  };

  // pid -> schemeKey -> { name, total, missing }
  // Counts the participant's OPEN rounds (that pass the active round
  // filters) per scheme, and how many of them have no DC yet.
  buildSchemeDcLookup = () => {
    const { SampleReport } = this.props;
    const covered = this.buildDcCoverage();
    const lookup = {};

    (SampleReport || []).forEach(scheme => {
      const key = normalize(scheme.name);

      (scheme.round_details || []).forEach(r => {
        if (!isRoundOpenForDispatch(r)) return;
        if (!this.roundPassesRoundFilters(scheme, r)) return;

        (r.participants || []).forEach(p => {
          if (p.id == null) return;

          const pid = p.id.toString();
          const byScheme = lookup[pid] || (lookup[pid] = {});
          const e =
            byScheme[key] ||
            (byScheme[key] = {
              name: scheme.name?.trim(),
              total: 0,
              missing: 0,
            });

          e.total += 1;
          if (!covered.has(`${pid}|${key}|${normalize(r.rounds)}`))
            e.missing += 1;
        });
      });
    });

    return lookup;
  };

  // null until the DC data has loaded (avoids a yellow flash on first paint).
  getSchemeDcSummary = pid => {
    if (!this.state.dcLinesLoaded) return null;

    const byScheme = this._schemeDcLookup?.[String(pid)];
    if (!byScheme) return { schemes: 0, pendingSchemes: 0, pendingNames: [] };

    const list = Object.values(byScheme);
    const pending = list.filter(s => s.missing > 0);

    return {
      schemes: list.length,
      pendingSchemes: pending.length,
      pendingNames: pending.map(s => s.name),
    };
  };

  // Loads a fresh SampleReport into local state while KEEPING the filters
  // the user has selected (as long as they are still valid for the data).
  applyReport = SampleReport => {
    const allSchemes = SampleReport.map(i => i.name?.trim());
    const schemes = this.getSchemesForDispatchType(
      this.state.dispatchTypeFilter,
      SampleReport
    );

    this.setState(
      {
        allSchemes,
        schemes,
        structuredData: this.formatTableData(SampleReport),
      },
      () => this.syncRoundOptions(SampleReport)
    );
  };

  // Rebuilds the Cycle / Round option lists for the current Scheme filter
  // and keeps the selected Scheme / Cycle / Round if they're still valid,
  // otherwise falls back to "All".
  syncRoundOptions = SampleReport => {
    const {
      schemes,
      selectedSchemeFilter,
      selectedCycleFilter,
      selectedRoundFilter,
      dispatchTypeFilter,
    } = this.state;

    const schemeValid =
      selectedSchemeFilter === "All" ||
      schemes.some(s => normalize(s) === normalize(selectedSchemeFilter));

    const scheme = schemeValid ? selectedSchemeFilter : "All";

    const { cycles } = this.computeRoundOptions(
      SampleReport,
      scheme,
      "All",
      dispatchTypeFilter
    );

    const cycleValid =
      schemeValid &&
      selectedCycleFilter !== "All" &&
      cycles.some(c => String(c) === String(selectedCycleFilter));

    const cycle = cycleValid ? selectedCycleFilter : "All";

    const { rounds } = this.computeRoundOptions(
      SampleReport,
      scheme,
      cycle,
      dispatchTypeFilter
    );

    const roundValid =
      schemeValid &&
      selectedRoundFilter !== "All" &&
      rounds.some(
        r =>
          String(r.rounds ?? "").trim() === String(selectedRoundFilter).trim()
      );

    this.setState({
      selectedSchemeFilter: scheme,
      cyclesForSelectedScheme: cycles,
      selectedCycleFilter: cycle,
      roundsForSelectedScheme: rounds,
      selectedRoundFilter: roundValid ? selectedRoundFilter : "All",
      selectedRoundParticipants: [],
    });
  };

  // Cycles + unique rounds for a scheme / cycle / dispatch type combo.
  computeRoundOptions = (SampleReport, scheme, cycle, dispatchType) => {
    const filterRounds = rounds => {
      if (!Array.isArray(rounds)) return [];
      return rounds.filter(r => roundMatchesDispatchType(r, dispatchType));
    };

    let rounds = [];
    if (scheme === "All") {
      (SampleReport || []).forEach(s => {
        rounds = [...rounds, ...filterRounds(s.round_details || [])];
      });
    } else {
      const obj = (SampleReport || []).find(
        s => normalize(s.name) === normalize(scheme)
      );
      rounds = filterRounds(obj?.round_details || []);
    }

    const cycles = Array.from(
      new Set(rounds.map(r => r.cycle_no).filter(Boolean))
    ).sort((a, b) => Number(a) - Number(b));

    if (cycle !== "All") {
      rounds = rounds.filter(r => r.cycle_no?.toString() === cycle.toString());
    }

    const uniqueRounds = Array.from(new Set(rounds.map(r => r.rounds))).map(n =>
      rounds.find(r => r.rounds === n)
    );

    return { cycles, rounds: uniqueRounds };
  };

  // ─── DC pending bell ──────────────────────────────────────────────────

  // Labs that still need DC work. A lab is listed when it has an open
  // round to dispatch AND any of these is true:
  //   • no DC created
  //   • DC created, but some schemes/rounds still have NO challan
  //   • DC created for everything, but not printed yet
  // Order: no DC first, then "some schemes pending", then "not printed".
  getPendingDcParticipants = () => {
    const { structuredData } = this.state;

    const rank = r => {
      if (!toBool(r.dc_created)) return 0;
      const s = this.getSchemeDcSummary(r.id);
      if (s && s.pendingSchemes > 0) return 1;
      return 2;
    };

    return (structuredData || [])
      .filter(r => {
        if (!r.round_status?.toLowerCase().includes("open")) return false;

        const s = this.getSchemeDcSummary(r.id);
        const partial = toBool(r.dc_created) && s && s.pendingSchemes > 0;

        return partial || !(toBool(r.dc_created) && toBool(r.dc_printing));
      })
      .sort((a, b) => {
        const diff = rank(a) - rank(b);
        if (diff !== 0) return diff;
        return String(a.lab_name || "").localeCompare(String(b.lab_name || ""));
      });
  };

  toggleBell = () =>
    this.setState(prev => ({
      bellOpen: !prev.bellOpen,
      // start with a fresh search box each time the bell opens
      bellSearch: prev.bellOpen ? "" : prev.bellSearch,
    }));

  // Clicking a lab name in the bell: reset every filter that could hide it
  // (the bell only lists labs with an open round, so "New" dispatches),
  // show ONLY that lab in the table, and scroll down to it.
  showParticipantInTable = p => {
    const schemes = this.getSchemesForDispatchType("New");

    this.setState(
      {
        dispatchTypeFilter: "New",
        schemes,
        selectedSchemeFilter: "All",
        selectedCycleFilter: "All",
        selectedDistrictFilter: ["All"],
        selectedCityFilter: ["All"],
        selectedTypeFilter: ["All"],
        selectedStatusFilter: "All",
        selectedRoundFilter: "All",
        selectedRoundPeriodFilter: "All",
        roundsForSelectedScheme: [],
        selectedRoundParticipants: [],
        focusedParticipantId: p.id,
        bellOpen: false,
        bellSearch: "",
      },
      () => {
        this.handleSchemeFilterChange("All");
        setTimeout(() => {
          this.tableTopRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
      }
    );
  };

  clearFocusedParticipant = () => this.setState({ focusedParticipantId: null });

  // ─── Toast ────────────────────────────────────────────────────────────────

  showToast = msg => {
    this.setState({ toastMessage: msg }, () =>
      setTimeout(() => this.setState({ toastMessage: "" }), 3000)
    );
  };

  // ─── Scheme helpers ─────────────────────────────────────────────────────

  getSchemesForDispatchType = (dispatchType, SampleReportOverride) => {
    const SampleReport = SampleReportOverride || this.props.SampleReport;
    return (SampleReport || [])
      .filter(s =>
        (s.round_details || []).some(r =>
          roundMatchesDispatchType(r, dispatchType)
        )
      )
      .map(s => s.name?.trim());
  };

  // (No longer used by the filters — kept for reference. All round-level
  // filtering now goes through getMatchingParticipantIds below.)
  getParticipantIdsForSchemeType = (schemeName, dispatchType) => {
    const { SampleReport } = this.props;
    const schemeObj = (SampleReport || []).find(
      s => normalize(s.name) === normalize(schemeName)
    );
    if (!schemeObj) return new Set();

    const rounds = (schemeObj.round_details || []).filter(r =>
      roundMatchesDispatchType(r, dispatchType)
    );

    const ids = new Set();
    rounds.forEach(r =>
      (r.participants || []).forEach(p => {
        if (p.id != null) ids.add(p.id.toString());
      })
    );
    return ids;
  };

  // (No longer used by the filters — kept for reference.)
  getParticipantIdsForRoundPeriod = (period, dispatchType) => {
    const { SampleReport } = this.props;
    const ids = new Set();

    (SampleReport || []).forEach(scheme => {
      (scheme.round_details || []).forEach(r => {
        if (!roundMatchesDispatchType(r, dispatchType)) return;
        if (normalize(r.round_period || "") !== normalize(period)) return;

        (r.participants || []).forEach(p => {
          if (p.id != null) ids.add(p.id.toString());
        });
      });
    });

    return ids;
  };

  // ✅ Returns a Set of participant ids that have AT LEAST ONE round
  // matching ALL the active round-level filters at the same time
  // (dispatch type, scheme, cycle, round name, round period).
  // Returns null when no round-level filter is active, meaning
  // "don't restrict by rounds".
  getMatchingParticipantIds = () => {
    const { SampleReport } = this.props;
    const {
      selectedSchemeFilter,
      selectedCycleFilter,
      selectedRoundFilter,
      selectedRoundPeriodFilter,
      dispatchTypeFilter,
    } = this.state;

    const noRoundFilters =
      selectedSchemeFilter === "All" &&
      selectedCycleFilter === "All" &&
      selectedRoundFilter === "All" &&
      selectedRoundPeriodFilter === "All";

    if (noRoundFilters) return null;

    const ids = new Set();

    (SampleReport || []).forEach(scheme => {
      if (
        selectedSchemeFilter !== "All" &&
        normalize(scheme.name) !== normalize(selectedSchemeFilter)
      )
        return;

      (scheme.round_details || []).forEach(r => {
        if (!roundMatchesDispatchType(r, dispatchTypeFilter)) return;

        if (
          selectedCycleFilter !== "All" &&
          String(r.cycle_no ?? "") !== String(selectedCycleFilter)
        )
          return;

        if (
          selectedRoundFilter !== "All" &&
          String(r.rounds ?? "").trim() !== String(selectedRoundFilter).trim()
        )
          return;

        if (
          selectedRoundPeriodFilter !== "All" &&
          normalize(r.round_period || "") !==
            normalize(selectedRoundPeriodFilter)
        )
          return;

        (r.participants || []).forEach(p => {
          if (p.id != null) ids.add(p.id.toString());
        });
      });
    });

    return ids;
  };

  // ✅ Does this round pass the active Scheme / Cycle / Round / Round
  // Period filters? (Dispatch type is checked separately.)
  roundPassesRoundFilters = (scheme, r) => {
    const {
      selectedSchemeFilter,
      selectedCycleFilter,
      selectedRoundFilter,
      selectedRoundPeriodFilter,
    } = this.state;

    if (
      selectedSchemeFilter !== "All" &&
      normalize(scheme?.name) !== normalize(selectedSchemeFilter)
    )
      return false;

    if (
      selectedCycleFilter !== "All" &&
      String(r.cycle_no ?? "") !== String(selectedCycleFilter)
    )
      return false;

    if (
      selectedRoundFilter !== "All" &&
      String(r.rounds ?? "").trim() !== String(selectedRoundFilter).trim()
    )
      return false;

    if (
      selectedRoundPeriodFilter !== "All" &&
      normalize(r.round_period || "") !== normalize(selectedRoundPeriodFilter)
    )
      return false;

    return true;
  };

  roundMatchesRoundFilters = (scheme, r) =>
    roundMatchesDispatchType(r, this.state.dispatchTypeFilter) &&
    this.roundPassesRoundFilters(scheme, r);

  // ✅ Scheme columns to show: only schemes that actually have a round
  // matching the active filters (e.g. Round Period "Next" → only the
  // scheme(s) whose Next round is open), instead of every scheme.
  getVisibleSchemes = () => {
    const { SampleReport } = this.props;
    const visible = new Set();

    (SampleReport || []).forEach(scheme => {
      if (
        (scheme.round_details || []).some(r =>
          this.roundMatchesRoundFilters(scheme, r)
        )
      )
        visible.add(normalize(scheme.name));
    });

    return (this.state.schemes || []).filter(s => visible.has(normalize(s)));
  };

  // ✅ Per participant + scheme: is the participant part of a matching
  // round, and have ALL those matching rounds been dispatched? Used for the
  // scheme checkboxes so they reflect only the filtered round(s), not every
  // round of the scheme.
  buildRoundLookup = () => {
    const { SampleReport } = this.props;
    const lookup = {};

    (SampleReport || []).forEach(scheme => {
      const schemeKey = normalize(scheme.name?.trim());

      (scheme.round_details || []).forEach(r => {
        if (!this.roundMatchesRoundFilters(scheme, r)) return;

        (r.participants || []).forEach(p => {
          if (p.id == null) return;
          const k = `${p.id}|${schemeKey}`;
          const entry = lookup[k] || (lookup[k] = { allGot: true });
          if (!p.got_sample) entry.allGot = false;
        });
      });
    });

    return lookup;
  };

  // ─── Filters ──────────────────────────────────────────────────────────────

  // Human-readable label for the (multi-select) District filter's current
  // value — mirrors getCityFilterDisplayLabel below.
  getDistrictFilterDisplayLabel = () => {
    const arr = this.state.selectedDistrictFilter || ["All"];
    if (arr.includes("All")) return "All Districts";
    return arr.join(", ");
  };

  // Human-readable label for the (multi-select) City filter's current value.
  getCityFilterDisplayLabel = () => {
    const arr = this.state.selectedCityFilter || ["All"];
    if (arr.includes("All")) return "All Cities";
    return arr
      .map(v => (v === GCC_FILTER_VALUE ? GCC_FILTER_LABEL : v))
      .join(", ");
  };

  // Human-readable label for the (multi-select) Lab Type filter's current
  // value — mirrors getCityFilterDisplayLabel above.
  getTypeFilterDisplayLabel = () => {
    const arr = this.state.selectedTypeFilter || ["All"];
    if (arr.includes("All")) return "All Types";
    return arr
      .map(v => (v === GCC_FILTER_VALUE ? GCC_FILTER_LABEL : v))
      .join(", ");
  };

  clearAllFilters = () =>
    this.setState({
      focusedParticipantId: null,
      selectedSchemeFilter: "All",
      selectedCycleFilter: "All",
      selectedDistrictFilter: ["All"],
      selectedCityFilter: ["All"],
      selectedTypeFilter: ["All"],
      selectedStatusFilter: "All",
      selectedRoundFilter: "All",
      selectedRoundPeriodFilter: "All",
      roundsForSelectedScheme: [],
      selectedRoundParticipants: [],
    });

  handleDispatchTypeChange = e => {
    const dispatchTypeFilter = e.target.value;
    const schemes = this.getSchemesForDispatchType(dispatchTypeFilter);

    this.setState(
      {
        dispatchTypeFilter,
        schemes,
        focusedParticipantId: null,
        selectedSchemeFilter: "All",
        selectedCycleFilter: "All",
        selectedDistrictFilter: ["All"],
        selectedCityFilter: ["All"],
        selectedTypeFilter: ["All"],
        selectedStatusFilter: "All",
        selectedRoundFilter: "All",
        selectedRoundPeriodFilter: "All", // reset when switching New/Previous
        roundsForSelectedScheme: [],
        selectedRoundParticipants: [],
      },
      () => this.handleSchemeFilterChange("All")
    );
  };

  handleSchemeFilterChange = eOrValue => {
    const selected =
      typeof eOrValue === "string" ? eOrValue : eOrValue.target.value;
    const { SampleReport } = this.props;
    const { dispatchTypeFilter } = this.state;

    const { cycles, rounds } = this.computeRoundOptions(
      SampleReport,
      selected,
      "All",
      dispatchTypeFilter
    );

    this.setState({
      selectedSchemeFilter: selected,
      cyclesForSelectedScheme: cycles,
      selectedCycleFilter: "All",
      roundsForSelectedScheme: rounds,
      selectedRoundFilter: "All",
      selectedRoundParticipants: [],
    });
  };

  handleCycleFilterChange = e => {
    const selectedCycle = e.target.value;
    const { SampleReport } = this.props;
    const { selectedSchemeFilter, dispatchTypeFilter } = this.state;

    const { rounds } = this.computeRoundOptions(
      SampleReport,
      selectedSchemeFilter,
      selectedCycle,
      dispatchTypeFilter
    );

    this.setState({
      selectedCycleFilter: selectedCycle,
      roundsForSelectedScheme: rounds,
      selectedRoundFilter: "All",
      selectedRoundParticipants: [],
    });
  };

  // ─── District filter (multi-select) ────────────────────────────────────
  // Selecting a District narrows down the options available in the City
  // filter (see getCityOptionsSource) to only cities that belong to the
  // selected District(s), and resets the City filter back to "All Cities"
  // since a previously selected city may no longer belong to the new
  // district(s). Selecting a City directly (with District left on "All")
  // still filters the table by that city as before, independently.

  toggleDistrictDropdown = () =>
    this.setState(prev => ({
      districtDropdownOpen: !prev.districtDropdownOpen,
      districtSearchTerm: prev.districtDropdownOpen
        ? ""
        : prev.districtSearchTerm,
    }));

  handleDistrictSearchChange = e => {
    this.setState({ districtSearchTerm: e.target.value });
  };

  handleDistrictCheckboxChange = value => {
    this.setState(prev => {
      // Checking "All Districts" clears everything else, and drops any
      // City restriction that was scoped to a specific district.
      if (value === "All")
        return {
          selectedDistrictFilter: ["All"],
          selectedCityFilter: ["All"],
        };

      // Picking any real value drops the "All" sentinel.
      const current = prev.selectedDistrictFilter.filter(d => d !== "All");

      const exists = current.includes(value);
      let next = exists
        ? current.filter(d => d !== value)
        : [...current, value];

      // Nothing left checked → fall back to "All" so the table never
      // silently ends up filtered down to zero rows.
      if (next.length === 0) next = ["All"];

      // Reset City filter whenever the district selection changes —
      // previously selected cities may not belong to the new district(s).
      return { selectedDistrictFilter: next, selectedCityFilter: ["All"] };
    });
  };

  // Cities available to pick in the City filter, narrowed down to
  // whichever District(s) are currently selected. Falls back to every
  // city in the data when no District is selected.
  getCityOptionsSource = () => {
    const { structuredData, selectedDistrictFilter } = this.state;
    const realDistrictFilters = (selectedDistrictFilter || []).filter(
      d => d !== "All"
    );
    if (!realDistrictFilters.length) return structuredData;
    return structuredData.filter(row =>
      realDistrictFilters.some(
        d => normalize(d) === normalize(row.district || "")
      )
    );
  };

  // ─── City filter (multi-select) ────────────────────────────────────────

  toggleCityDropdown = () =>
    this.setState(prev => ({
      cityDropdownOpen: !prev.cityDropdownOpen,
      // Clear the search box whenever the dropdown closes, so it starts
      // fresh next time it's opened.
      citySearchTerm: prev.cityDropdownOpen ? "" : prev.citySearchTerm,
    }));

  handleCitySearchChange = e => {
    const value = e.target.value;
    this.setState({ citySearchTerm: value });
  };

  handleCityCheckboxChange = value => {
    this.setState(prev => {
      // Checking "All Cities" clears everything else.
      if (value === "All") return { selectedCityFilter: ["All"] };

      // Picking any real value drops the "All" sentinel.
      const current = prev.selectedCityFilter.filter(c => c !== "All");

      const exists = current.includes(value);
      let next = exists
        ? current.filter(c => c !== value)
        : [...current, value];

      // Nothing left checked → fall back to "All" so the table
      // never silently ends up filtered down to zero rows.
      if (next.length === 0) next = ["All"];

      return { selectedCityFilter: next };
    });
  };

  // ─── Lab Type filter (multi-select) ────────────────────────────────────
  // Same shape and behavior as the City filter above: an array of chosen
  // values, with "All" as the sentinel meaning "no type restriction".

  toggleTypeDropdown = () =>
    this.setState(prev => ({ typeDropdownOpen: !prev.typeDropdownOpen }));

  handleTypeCheckboxChange = value => {
    this.setState(prev => {
      // Checking "All Types" clears everything else.
      if (value === "All") return { selectedTypeFilter: ["All"] };

      // Picking any real value drops the "All" sentinel.
      const current = prev.selectedTypeFilter.filter(t => t !== "All");

      const exists = current.includes(value);
      let next = exists
        ? current.filter(t => t !== value)
        : [...current, value];

      // Nothing left checked → fall back to "All" so the table
      // never silently ends up filtered down to zero rows.
      if (next.length === 0) next = ["All"];

      return { selectedTypeFilter: next };
    });
  };

  // ✅ The table now reads the selected round name directly (see
  // getMatchingParticipantIds), so this only needs to store the value.
  handleRoundFilterChange = e => {
    this.setState({ selectedRoundFilter: e.target.value });
  };

  // ✅ NEW: Builds the participant's dispatched rounds straight from
  // round_details (matched by participant + round), so it doesn't depend on
  // the participant also being in scheme.participants (which only lists
  // participants with an active Payment / SelectedScheme). This is what the
  // "Already Dispatched" text and the Edit (pencil) icon rely on.
  getSubmittedReportsFromRounds = participantId => {
    const { SampleReport } = this.props;
    const pid = participantId?.toString();
    const reports = [];

    (SampleReport || []).forEach(scheme => {
      (scheme.round_details || []).forEach(round => {
        const p = (round.participants || []).find(
          x => x.id?.toString() === pid
        );

        if (p && p.got_sample) {
          reports.push({
            scheme_name: scheme.name,
            round_name: round.rounds,
            round_id: round.round_id,
            dispatch_date: p.dispatch_date,
            tracking_id: p.tracking_id,
            date_of_delivery: p.date_of_delivery,
            comments: p.comments,
          });
        }
      });
    });

    return reports;
  };

  isFullyDispatched = row => {
    const { SampleReport } = this.props;

    const openRounds = [];
    const dispatchedRounds = [];

    (SampleReport || []).forEach(scheme => {
      (scheme.round_details || []).forEach(round => {
        const participantExists = (round.participants || []).some(
          p => p.id?.toString() === row.id?.toString()
        );

        // ✅ Only count rounds that are open AND open_for "Both"/"Customer
        // Service" — participant-only open rounds aren't this report's
        // concern. Also limited to rounds passing the active round filters.
        if (
          participantExists &&
          isRoundOpenForDispatch(round) &&
          this.roundPassesRoundFilters(scheme, round)
        ) {
          openRounds.push(`${scheme.name}_${round.rounds}`);
        }
      });
    });

    let submittedReports = [];

    (SampleReport || []).forEach(scheme => {
      (scheme.participants || []).forEach(p => {
        if (p.id?.toString() === row.id?.toString()) {
          submittedReports = [
            ...submittedReports,
            ...(p.submitted_reports || []),
          ];
        }
      });
    });

    submittedReports.forEach(report => {
      if (report?.tracking_id) {
        dispatchedRounds.push(`${report.scheme_name}_${report.round_name}`);
      }
    });

    return (
      openRounds.length > 0 &&
      openRounds.every(round => dispatchedRounds.includes(round))
    );
  };

  isPartiallyDispatched = row => {
    const { SampleReport } = this.props;

    const openRounds = [];
    const dispatchedRounds = [];

    (SampleReport || []).forEach(scheme => {
      (scheme.round_details || []).forEach(round => {
        const participantExists = (round.participants || []).some(
          p => p.id?.toString() === row.id?.toString()
        );

        // ✅ Same open_for + round-filter restriction as isFullyDispatched.
        if (
          participantExists &&
          isRoundOpenForDispatch(round) &&
          this.roundPassesRoundFilters(scheme, round)
        ) {
          openRounds.push(`${scheme.name}_${round.rounds}`);
        }
      });
    });

    (SampleReport || []).forEach(scheme => {
      (scheme.participants || []).forEach(p => {
        if (p.id?.toString() === row.id?.toString()) {
          (p.submitted_reports || []).forEach(report => {
            if (report?.tracking_id) {
              dispatchedRounds.push(
                `${report.scheme_name}_${report.round_name}`
              );
            }
          });
        }
      });
    });

    const matchedDispatched = openRounds.filter(round =>
      dispatchedRounds.includes(round)
    );

    return (
      matchedDispatched.length > 0 &&
      matchedDispatched.length < openRounds.length
    );
  };

  // ✅ UPDATED: reads from round_details, so the Edit (pencil) icon shows
  // whenever the participant has a dispatch saved for any round.
  hasAnyDispatchedRound = row =>
    this.getSubmittedReportsFromRounds(row.id).some(r => !!r.tracking_id);

  // ✅ Single source of truth for the table rows AND the stat cards.
  // Pass ignoreStatus=true to skip the Dispatched / Pending / Partially
  // Dispatched filter (used by getDispatchStats so the cards always show
  // the full breakdown for the other selected filters).
  getFilteredData = (ignoreStatus = false) => {
    const {
      structuredData,
      selectedDistrictFilter,
      selectedCityFilter,
      dispatchTypeFilter,
      selectedTypeFilter,
      selectedStatusFilter,
    } = this.state;

    if (!structuredData?.length) return [];
    let data = [...structuredData];

    // round_status is built in formatTableData, which already only marks
    // a round "open" there when it passes isRoundOpenForDispatch — so this
    // check inherits the open_for restriction automatically.
    if (dispatchTypeFilter === "New")
      data = data.filter(r => r.round_status?.toLowerCase().includes("open"));
    if (dispatchTypeFilter === "Previous")
      data = data.filter(r =>
        ["closed", "report available"].includes(r.round_status?.toLowerCase())
      );

    // ✅ Scheme + Cycle + Round + Round Period, all checked against the
    // SAME round, so the filters can no longer contradict each other.
    const matchingIds = this.getMatchingParticipantIds();
    if (matchingIds) {
      data = data.filter(
        r =>
          matchingIds.has(r.participant_id?.toString()) ||
          matchingIds.has(r.id?.toString())
      );
    }

    if (!ignoreStatus) {
      if (selectedStatusFilter === "Dispatched")
        data = data.filter(row => this.isFullyDispatched(row));

      if (selectedStatusFilter === "Pending")
        data = data.filter(row => !this.isFullyDispatched(row));

      if (selectedStatusFilter === "Partially Dispatched")
        data = data.filter(row => this.isPartiallyDispatched(row));
    }

    // ── District handling ──────────────────────────────────────────────
    const realDistrictFilters = (selectedDistrictFilter || []).filter(
      d => d !== "All"
    );

    if (realDistrictFilters.length > 0)
      data = data.filter(r =>
        realDistrictFilters.some(
          d => normalize(d) === normalize(r.district || "")
        )
      );

    // ── City / Lab Type / GCC handling ─────────────────────────────────
    // GCC rows are NEVER stripped out of the dataset just because nothing
    // is selected. They only get narrowed down to GCC-only when GCC is
    // explicitly requested (via the "All GCC Labs" shortcut in either
    // dropdown, or by picking a real "GCC" value from Lab Type).
    //
    // This means the table's own in-column text filters (District, Lab
    // Type, Participant Address, etc.) can always find GCC rows too —
    // they're part of the normal dataset, not silently removed upstream.
    //
    // Both City and Lab Type are multi-select: arrays of chosen values.
    // "All" is the sentinel meaning "no restriction" and is ignored here.
    const realCityFilters = selectedCityFilter.filter(
      c => c !== "All" && c !== GCC_FILTER_VALUE
    );

    if (realCityFilters.length > 0)
      data = data.filter(r =>
        realCityFilters.some(c => normalize(c) === normalize(r.city || ""))
      );

    const realTypeFilters = selectedTypeFilter.filter(
      t => t !== "All" && t !== GCC_FILTER_VALUE
    );

    if (realTypeFilters.length > 0)
      data = data.filter(r =>
        realTypeFilters.some(t => normalize(t) === normalize(r.type || ""))
      );

    const wantsGCC =
      selectedCityFilter.includes(GCC_FILTER_VALUE) ||
      selectedTypeFilter.includes(GCC_FILTER_VALUE) ||
      realTypeFilters.some(t => normalize(t).includes("gcc"));

    if (wantsGCC) {
      // Explicitly asked for GCC → narrow down to only GCC rows.
      data = data.filter(r => normalize(r.type || "").includes("gcc"));
    }
    // else: GCC rows stay in the dataset as-is — no default hiding.

    return data;
  };

  // ─── Data Formatting ──────────────────────────────────────────────────────
  formatTableData = data => {
    const map = {};

    data.forEach(scheme => {
      const schemeName = scheme.name?.trim() || "unnamed_scheme";
      const schemeKey = normalize(schemeName);

      const paymentParticipants = Array.isArray(scheme.participants)
        ? scheme.participants
        : [];
      const rounds = scheme.round_details || [];

      const participantsById = {};
      paymentParticipants.forEach(p => {
        const pid = p.id?.toString();
        if (pid) participantsById[pid] = p;
      });
      rounds.forEach(rnd => {
        (rnd.participants || []).forEach(p => {
          const pid = p.id?.toString();
          if (pid && !participantsById[pid]) participantsById[pid] = p;
        });
      });
      const participants = Object.values(participantsById);

      participants.forEach(p => {
        const pid = p.id?.toString();
        if (!pid) return;

        // ─── init participant ─────────────────────────────
        if (!map[pid]) {
          map[pid] = {
            id: pid,
            participant_id: pid,
            lab_name: p.name || "unnamed_lab",
            district: p.district || "-",
            city: p.city || "-",
            type: p.type || "-",
            lab_staff_name: p.lab_staff_name || "-",
            dispatch_date: p.dispatch_date || "-",
            tracking_id: p.tracking_id || "-",
            date_of_delivery: p.date_of_delivery || "-",
            shipping_address: p.shipping_address || "-",
            landline_registered_by: p.landline_registered_by || "-",
            comments: p.comments || "-",
            dc_created: toBool(p.dc_created),
            dc_printing: toBool(p.dc_printing),
            round_statuses: [],
            round_status: "",
            round_id: null,
          };
        } else {
          // ─── enrich with better metadata from a later record ───
          const enrich = (field, value, placeholder) => {
            if (
              map[pid][field] === placeholder &&
              value &&
              value !== placeholder
            ) {
              map[pid][field] = value;
            }
          };
          enrich("lab_name", p.name, "unnamed_lab");
          enrich("district", p.district, "-");
          enrich("city", p.city, "-");
          enrich("type", p.type, "-");
          enrich("lab_staff_name", p.lab_staff_name, "-");
          enrich("dispatch_date", p.dispatch_date, "-");
          enrich("tracking_id", p.tracking_id, "-");
          enrich("date_of_delivery", p.date_of_delivery, "-");
          enrich("shipping_address", p.shipping_address, "-");
          enrich("landline_registered_by", p.landline_registered_by, "-");
          enrich("comments", p.comments, "-");

          // dc flags are participant-wide; if ANY record for this
          // participant says true, the row is true.
          if (p.dc_created !== undefined && p.dc_created !== null) {
            map[pid].dc_created = toBool(p.dc_created);
          }

          if (p.dc_printing !== undefined && p.dc_printing !== null) {
            map[pid].dc_printing = toBool(p.dc_printing);
          }
        }

        // ─── IMPORTANT: initialize scheme column ────────
        if (map[pid][schemeKey] === undefined) {
          map[pid][schemeKey] = false;
        }

        // ─── evaluate rounds ─────────────────────────────
        rounds.forEach(rnd => {
          const found = (rnd.participants || []).find(
            x => x.id?.toString() === pid
          );

          if (!found) return;

          if (found.got_sample) {
            map[pid][schemeKey] = true;
          }

          // ✅ Only let a round's DC Created / DC Printing flags drive the
          // checkbox display when that round is NOT currently open for
          // dispatch. An Open round hasn't been dispatched yet, so its
          // dc_created/dc_printing values shouldn't be shown as checked
          // here — only closed / report-available rounds should.
          if (!isRoundOpenForDispatch(rnd)) {
            if (found.dc_created !== undefined && found.dc_created !== null) {
              map[pid].dc_created = toBool(found.dc_created);
            }

            if (found.dc_printing !== undefined && found.dc_printing !== null) {
              map[pid].dc_printing = toBool(found.dc_printing);
            }
          }

          if (rnd.status) {
            const st = rnd.status.toLowerCase();
            // ✅ If this round is "open" but its open_for doesn't include
            // Customer Service (i.e. it's Participant-only), don't let it
            // count as "open" for this report's round_status rollup —
            // otherwise a participant-only open round would still make
            // this row show up under "New Dispatches".
            if (st === "open" && !isRoundOpenForDispatch(rnd)) {
              // skip — not open for dispatch purposes here
            } else {
              map[pid].round_statuses.push(st);
            }
          }

          map[pid].round_id = rnd.round_id || map[pid].round_id;
        });
      });
    });

    return Object.values(map).map(row => ({
      ...row,
      round_status: row.round_statuses.includes("open")
        ? "open"
        : row.round_statuses[row.round_statuses.length - 1] || "",
    }));
  };

  // ─── Modal ────────────────────────────────────────────────────────────────

  toggleModal = (row = null, mode = null) => {
    if (row) {
      const { SampleReport } = this.props;
      const existingValues = this.state.editValues[row.id] || {};

      // ✅ UPDATED: dispatched rounds come straight from round_details
      // (participant + round), not from scheme.participants.
      const uniqueReports = this.getSubmittedReportsFromRounds(
        row.participant_id || row.id
      );

      const participantRounds = [];
      (SampleReport || []).forEach(s =>
        (s.round_details || []).forEach(r =>
          (r.participants || []).forEach(p => {
            if (p.id === Number(row.participant_id || row.id)) {
              const rn = r.rounds || r.round_name || "";
              if (rn && !participantRounds.includes(rn))
                participantRounds.push(rn);
            }
          })
        )
      );

      const initialChecked = {};
      if (mode === "update") {
        uniqueReports.forEach(r => {
          if (r?.tracking_id && r?.scheme_name && r?.round_name) {
            initialChecked[`checked_${r.scheme_name}_${r.round_name}`] = true;
          }
        });
      }

      const dispatchDateStr =
        existingValues.dispatchDate ||
        (row.dispatch_date && row.dispatch_date !== "-"
          ? row.dispatch_date.slice(0, 10)
          : "");

      const modalSchemes =
        mode === "add"
          ? (row.purchased_schemes || []).length > 0
            ? row.purchased_schemes
            : (SampleReport || [])
                .filter(s =>
                  // ✅ open_for restriction applied here too.
                  s.round_details?.some(r => isRoundOpenForDispatch(r))
                )
                .map(s => s.name)
          : [];

      // NOTE: this used to also overwrite `selectedRoundFilter` with the
      // participant's round names. That silently changed the page's
      // "Filter by Round" dropdown (and now the table filtering) every
      // time a modal was opened, so it has been removed.
      this.setState({
        modalMode: mode || "add",
        selectedRowData: {
          ...row,
          submitted_reports: uniqueReports,
          ...initialChecked,
        },
        formData: {
          dispatchDate: dispatchDateStr,
          trackingId:
            existingValues.trackingId ||
            (row.tracking_id === "-" ? "" : row.tracking_id) ||
            "",
          comments: existingValues.comments || row.comments || "",
        },
        modalSchemes,
        modalOpen: true,
      });
      document.body.style.overflow = "hidden";
    } else {
      this.setState(
        {
          modalOpen: false,
          selectedRowData: null,
          formErrors: {},
          formData: INIT_FORM,
        },
        () => {
          document.body.style.overflow = "auto";
          const { user_id } = this.state;
          if (user_id) this.props.onGetSampleReport(user_id);
        }
      );
    }
  };

  // ✅ UPDATED: submitted_reports now come from round_details via
  // getSubmittedReportsFromRounds, so "Already Dispatched" shows correctly
  // and no longer stamps every report with the current scheme's name.
  handleAddClick = participant => {
    const { SampleReport } = this.props;
    const pid = participant.id?.toString();

    const purchasedSchemes = [];

    (SampleReport || []).forEach(scheme => {
      const inPaymentList = (scheme.participants || []).some(
        p => p.id?.toString() === pid
      );
      const inRoundList = (scheme.round_details || []).some(round =>
        (round.participants || []).some(p => p.id?.toString() === pid)
      );
      if (!(inPaymentList || inRoundList)) return;

      // ✅ open_for restriction applied here too.
      const hasOpenRound = (scheme.round_details || []).some(round =>
        isRoundOpenForDispatch(round)
      );
      if (!hasOpenRound) return;

      purchasedSchemes.push(scheme.name);
    });

    this.setState({
      modalMode: "add",
      modalOpen: true,
      selectedRowData: {
        ...participant,
        submitted_reports: this.getSubmittedReportsFromRounds(pid),
      },
      modalSchemes: purchasedSchemes,
      formData: {
        ...INIT_FORM,
        dispatchDate: new Date().toISOString().slice(0, 10),
      },
      formErrors: {},
    });

    document.body.style.overflow = "hidden";
  };

  handleEditClick = row => this.toggleModal(row, "update");

  handleFormChange = e => {
    const { name, value } = e.target;
    this.setState(prev => {
      const formData = { ...prev.formData, [name]: value };
      const formErrors = { ...prev.formErrors };
      return { formData, formErrors };
    });
  };

  handleSave = async () => {
    const { selectedRowData, formData, user_id, modalMode } = this.state;
    const { SampleReport } = this.props;
    if (!selectedRowData) return;

    const errors = {};
    if (!formData.dispatchDate)
      errors.dispatchDate = "Dispatch date is required.";

    if (!formData.trackingId?.trim())
      errors.trackingId = "Tracking ID is required.";
    if (!formData.comments?.trim()) errors.comments = "Comments are required.";

    if (Object.keys(errors).length) {
      this.setState({ formErrors: errors });
      return;
    }

    let dispatchedSchemes = [];
    Object.keys(selectedRowData).forEach(key => {
      if (key.startsWith("checked_") && selectedRowData[key]) {
        const [, scheme, roundName] = key.split("_");
        const roundObj = SampleReport.find(
          s => s.name?.trim().toLowerCase() === scheme?.trim().toLowerCase()
        )?.round_details?.find(
          r =>
            r.rounds?.trim().toLowerCase() === roundName?.trim().toLowerCase()
        );
        if (roundObj)
          dispatchedSchemes.push({
            scheme_name: scheme,
            round_name: roundObj.rounds,
            round_id: Number(roundObj.round_id),
          });
      }
    });

    if (!dispatchedSchemes.length) {
      this.setState({
        formErrors: {
          ...errors,
          roundSelection:
            modalMode === "add"
              ? "Please select at least one round to dispatch."
              : "Please select at least one round to update.",
        },
      });
      return;
    }

    this.setState({ formErrors: {} });

    const payload = {
      participant: selectedRowData.id,
      participant_name: selectedRowData.lab_name,
      district: selectedRowData.district,
      city: selectedRowData.city,
      lab_staff_name: selectedRowData.lab_staff_name,
      scheme_name: dispatchedSchemes.map(s => s.scheme_name).join(", "),
      dispatch_date: formData.dispatchDate || null,
      tracking_id: formData.trackingId,
      comments: formData.comments,
      added_by: user_id,
      dispatched_schemes: dispatchedSchemes,
      full_sync: true,
      round_id:
        dispatchedSchemes.length > 0
          ? Number(dispatchedSchemes[0].round_id)
          : null,
      round_name: dispatchedSchemes.map(s => s.round_name).join(", "),
    };

    if (!payload.added_by) {
      this.showToast("❌ Unable to identify current user (added_by missing).");
      return;
    }

    try {
      if (modalMode === "add") await this.props.onPostSampleReport(payload);
      else await this.props.onUpdateSampleReport(payload);
      await this.props.onGetSampleReport(user_id);
      this.showToast(
        modalMode === "add"
          ? "✅ Dispatch saved successfully!"
          : "✅ Dispatch updated successfully!"
      );
      this.toggleModal();
    } catch (err) {
      console.error("❌ Error saving/updating dispatch:", err);
    }
  };

  // ─── DC status toggle ───────────────────────────────────────────────────
  // Optimistically flips the checkbox and rolls back on failure so the UI
  // never silently drifts from the server. The row's background color is
  // derived from dc_created / dc_printing via getRowBackground below.
  handleDcToggle = async (row, field) => {
    const newValue = !row[field];

    this.setState(prev => ({
      structuredData: prev.structuredData.map(r =>
        r.id === row.id ? { ...r, [field]: newValue } : r
      ),
    }));

    try {
      await axios.post(
        `https://externalqcapi.com/api/registration-admin/participant/${row.id}/dc-status/`,
        {
          field,
          value: newValue,
          added_by: this.state.user_id,
        }
      );
    } catch (err) {
      console.error("❌ Failed to update DC status:", err);
      this.setState(prev => ({
        structuredData: prev.structuredData.map(r =>
          r.id === row.id ? { ...r, [field]: !newValue } : r
        ),
      }));
      this.showToast("❌ Failed to update status, please try again.");
    }
  };

  // ─── Row coloring based on DC status ───────────────────────────────────
  // Yellow when DC is created but some schemes/rounds still have NO
  // challan, red when only DC Created is checked (not printed yet), green
  // when both are checked and every scheme has a challan.
  getRowBackground = row => {
    const dcCreated = toBool(row?.dc_created);
    const dcPrinting = toBool(row?.dc_printing);

    // DC created, but some schemes are still pending
    const summary = this.getSchemeDcSummary(row?.id);
    if (dcCreated && summary && summary.pendingSchemes > 0) {
      return "#fff3cd";
    }

    // Both DC Created and DC Printing completed
    if (dcCreated && dcPrinting) {
      return "#d4edda";
    }

    // DC Created completed but DC Printing not completed
    if (dcCreated && !dcPrinting) {
      return "#f8d7da";
    }

    // Nothing created yet
    return undefined;
  };

  // ─── Delivery Challan navigation ───────────────────────────────────────
  // Same tab: passes the participant via router state (filters on this page
  // are persisted, so they're still here when the user comes back).
  // New tab (truck icon): a new tab has no router state, so the
  // participant id + round period travel in the URL instead.
  // Repeat DC (repeat = true): same page, opened with ?mode=repeat so it
  // shows the DCs already created and offers a "New DC" on a new date.
  openDeliveryChallan = (row, inNewTab = false, repeat = false) => {
    const org = this.props.match.params.organization_name;
    const pathname = `/${org}/Delivery_Challan`;
    const roundPeriodFilter = this.state.selectedRoundPeriodFilter;

    if (inNewTab) {
      const params = new URLSearchParams();
      params.set("participant_id", row.id);
      if (repeat) params.set("mode", "repeat");
      if (roundPeriodFilter && roundPeriodFilter !== "All") {
        params.set("round_period", roundPeriodFilter);
      }

      const href = this.props.history.createHref({
        pathname,
        search: `?${params.toString()}`,
      });

      openInNewTab(href);
      return;
    }

    this.props.history.push(pathname, {
      participant: row,
      roundPeriodFilter,
    });
  };

  // ─── PDF ──────────────────────────────────────────────────────────────────

  downloadAllParticipantsPDF = () => {
    if (!this.getFilteredData().length) {
      alert("No data available to download");
      return;
    }
    this.setState({ showDownloadConfirm: true });
  };

  proceedDownloadPDF = () => {
    this.setState({ showDownloadConfirm: false });
    const data = this.getFilteredData();
    const {
      selectedSchemeFilter,
      selectedCycleFilter,
      selectedRoundFilter,
      selectedDistrictFilter,
      selectedCityFilter,
      selectedTypeFilter,
      selectedStatusFilter,
      selectedRoundPeriodFilter,
      dispatchTypeFilter,
    } = this.state;

    const doc = new jsPDF();
    const visibleSchemes = this.getVisibleSchemes();
    const roundLookup = this.buildRoundLookup();
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const col1X = 14;
    const col2X = 115;
    const col1W = 60;
    const col2W = 75;
    const lh = 5.5;
    const rightM = 14;

    let y = 20;

    doc.setFontSize(12);
    doc.setFont("Helvetica", "bold");
    doc.text("Sample Dispatch Details", col1X, y);
    doc.setFont("Helvetica", "normal");
    y += 7;

    const filters = [
      dispatchTypeFilter !== "All" && `Type: ${dispatchTypeFilter}`,
      selectedSchemeFilter !== "All" && `Scheme: ${selectedSchemeFilter}`,
      selectedCycleFilter !== "All" && `Cycle: ${selectedCycleFilter}`,
      selectedRoundFilter !== "All" && `Round: ${selectedRoundFilter}`,
      selectedRoundPeriodFilter !== "All" &&
        `Round Period: ${selectedRoundPeriodFilter}`,
      !selectedDistrictFilter.includes("All") &&
        `District: ${this.getDistrictFilterDisplayLabel()}`,
      !selectedCityFilter.includes("All") &&
        `City: ${this.getCityFilterDisplayLabel()}`,
      !selectedTypeFilter.includes("All") &&
        `Lab Type: ${this.getTypeFilterDisplayLabel()}`,
      selectedStatusFilter !== "All" && `Status: ${selectedStatusFilter}`,
    ].filter(Boolean);

    if (filters.length) {
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc
        .splitTextToSize(filters.join("  |  "), pageW - col1X * 2)
        .forEach(l => {
          doc.text(l, col1X, y);
          y += 4;
        });
      doc.setTextColor(0);
    }
    y += 3;

    data.forEach(p => {
      doc.setFontSize(10);
      doc.setFont("Helvetica", "normal");

      const labLines = doc.splitTextToSize(p.lab_name || "-", col1W);

      const fullAddress = [p.shipping_address, p.district]
        .filter(x => x && x !== "-")
        .join(", ");
      const addrLines = doc.splitTextToSize(fullAddress || "-", col1W);

      const cityLines = doc.splitTextToSize(p.city || "-", col1W);

      doc.setFont("Helvetica", "bold");
      const attnLabel = "Attention: ";
      const attnLabelW = doc.getTextWidth(attnLabel);
      const attnLines =
        p.lab_staff_name && p.lab_staff_name !== "-"
          ? doc.splitTextToSize(p.lab_staff_name, col1W - attnLabelW)
          : [];

      const contactLines =
        p.landline_registered_by && p.landline_registered_by !== "-"
          ? doc.splitTextToSize(`Contact: ${p.landline_registered_by}`, col1W)
          : [];

      doc.setFont("Helvetica", "normal");

      const leftLineCount =
        labLines.length +
        addrLines.length +
        cityLines.length +
        attnLines.length +
        contactLines.length;
      const estH1 = leftLineCount * lh;

      let estH2 = 0;
      visibleSchemes.forEach(s => {
        if (!roundLookup[`${p.id}|${normalize(s)}`]) return;
        estH2 += doc.splitTextToSize(s, col2W).length * lh;
      });

      const blockH = Math.max(estH1, estH2) + 6;
      if (y + blockH > pageH - 14) {
        doc.addPage();
        y = 20;
      }

      let h1 = 0;
      doc.setFontSize(10);

      doc.setFont("Helvetica", "bold");
      labLines.forEach(t => {
        doc.text(t, col1X, y + h1);
        h1 += lh;
      });

      doc.setFont("Helvetica", "normal");
      addrLines.forEach(t => {
        doc.text(t, col1X, y + h1);
        h1 += lh;
      });

      doc.setFont("Helvetica", "bold");
      cityLines.forEach(t => {
        doc.text(t, col1X, y + h1);
        h1 += lh;
      });

      if (attnLines.length) {
        doc.setFont("Helvetica", "normal");
        doc.text(attnLabel, col1X, y + h1);
        doc.setFont("Helvetica", "bold");
        attnLines.forEach((t, i) => {
          doc.text(t, col1X + (i === 0 ? attnLabelW : 0), y + h1);
          h1 += lh;
        });
      }

      if (contactLines.length) {
        doc.setFont("Helvetica", "bold");
        contactLines.forEach(t => {
          doc.text(t, col1X, y + h1);
          h1 += lh;
        });
      }

      doc.setFont("Helvetica", "normal");

      let cy = y;
      visibleSchemes.forEach(s => {
        const entry = roundLookup[`${p.id}|${normalize(s)}`];
        if (!entry) return;

        const bs = 3.5;
        const bx = col2X;
        const by = cy - bs + 0.5;
        doc.rect(bx, by, bs, bs);

        if (entry.allGot === true) {
          doc.setLineWidth(0.5);
          doc.line(bx + 0.5, by + 1.8, bx + 1.4, by + bs - 0.5);
          doc.line(bx + 1.4, by + bs - 0.5, bx + bs - 0.3, by + 0.3);
          doc.setLineWidth(0.2);
        }

        doc.setFontSize(10);
        doc.splitTextToSize(s, col2W).forEach(t => {
          doc.text(t, col2X + 6, cy);
          cy += lh;
        });
      });

      y += Math.max(h1, cy - y) + 4;
      doc.setDrawColor(200, 200, 200);
      doc.line(col1X, y, pageW - rightM, y);
      doc.setDrawColor(0);
      y += 4;
    });

    const total = doc.internal.getNumberOfPages();
    const now = new Date();
    const dts =
      now.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }) +
      "  " +
      now.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });

    for (let i = 1; i <= total; i++) {
      doc.setPage(i);
      const fy = pageH - 8;
      doc.setDrawColor(180, 180, 180);
      doc.line(col1X, fy - 4, pageW - rightM, fy - 4);
      doc.setDrawColor(0);
      doc.setFontSize(9);
      doc.setFont("Helvetica", "normal");
      doc.setTextColor(100);
      const pt = `${i} / ${total}`;
      doc.text(pt, (pageW - doc.getTextWidth(pt)) / 2, fy);
      if (i === total) {
        doc.setFontSize(8);
        doc.text(dts, pageW - rightM - doc.getTextWidth(dts), fy);
      }
      doc.setTextColor(0);
    }

    doc.save("sample_dispatch_report.pdf");
  };

  // ─── Columns ──────────────────────────────────────────────────────────────

  generateColumns() {
    // Only schemes with a round matching the active filters get a column.
    const schemes = this.getVisibleSchemes();

    const fixedColumns = [
      {
        dataField: "district",
        text: "District",
        sort: true,
        filter: tf(),
        headerStyle: {
          width: "110px",
          textAlign: "center",
          position: "sticky",
          top: 0,
          zIndex: 4,
          background: "#fff",
        },
        style: (cell, row) => ({
          fontSize: "12px",
          padding: "4px",
          background: this.getRowBackground(row),
        }),
      },
      {
        dataField: "type",
        text: "Lab Type",
        filter: tf(),
        headerStyle: {
          width: "90px",
          textAlign: "center",
          position: "sticky",
          top: 0,
          zIndex: 4,
          background: "#fff",
        },
        style: (cell, row) => ({
          fontSize: "11px",
          textAlign: "center",
          background: this.getRowBackground(row),
        }),
        formatter: cell => (cell && cell !== "-" ? cell : "-"),
      },
      {
        dataField: "lab_name",
        text: "Participant Address",
        sort: true,
        filter: tf(),
        headerStyle: {
          width: "260px",
          minWidth: "260px",
          maxWidth: "260px",
          textAlign: "center",
          position: "sticky",
          left: "110px",
          top: 0,
          zIndex: 5,
          background: "#fff",
        },
        // Every column's `style` now paints its own DC-status background
        // (see getRowBackground) instead of relying on the table's
        // rowStyle — the table's default CSS gives each <td> its own
        // opaque background, which hid a row-level color except on
        // hover. This column additionally needs `|| "#fff"` since it's
        // sticky and must stay opaque against the columns scrolling
        // underneath it.
        style: (cell, row) => ({
          width: "260px",
          minWidth: "260px",
          maxWidth: "260px",
          position: "sticky",
          left: "110px",
          zIndex: 2,
          background: this.getRowBackground(row) || "#fff",
          textAlign: "left",
        }),
        formatter: (cell, row) => (
          <div
            style={{
              whiteSpace: "pre-line",
              lineHeight: "1.4",
              fontSize: "12px",
              color: "#555",
            }}
          >
            <span style={{ color: "#000", fontSize: "14px" }}>
              {row.lab_name || "-"}
            </span>
            <br />
            {row.lab_staff_name && <span>{row.lab_staff_name}</span>}
            <br />
            {row.shipping_address && <span>{row.shipping_address}</span>}
            <br />
            {row.city && <span>{row.city}</span>}
            <br />
            {row.district && <span>{row.district}</span>}
            <br />
            {row.landline_registered_by && (
              <span>{row.landline_registered_by}</span>
            )}
          </div>
        ),
      },
    ];

    // ── DC Created / DC Printing columns, before the scheme columns ──
    // The coloring is applied per-cell (via `style` below) rather than
    // relying only on the table's rowStyle — some cells in this table
    // (Bootstrap's table CSS, the sticky columns) paint their own opaque
    // background, which otherwise hides a row-level background color.
    const dcColumn = (field, label) => ({
      dataField: field,
      text: label,
      headerStyle: {
        width: "80px",
        textAlign: "center",
        position: "sticky",
        top: 0,
        zIndex: 4,
        background: "#fff",
      },
      style: (cell, row) => ({
        width: "80px",
        textAlign: "center",
        padding: "0",
        background: this.getRowBackground(row),
      }),
      formatter: (cell, row) => (
        <div
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "6px 0",
          }}
        >
          <input
            type="checkbox"
            checked={toBool(cell)}
            onChange={() => this.handleDcToggle(row, field)}
            style={{ cursor: "pointer" }}
          />
        </div>
      ),
    });

    const dcColumns = [
      dcColumn("dc_created", "DC Created"),
      dcColumn("dc_printing", "DC Printing"),
    ];

    // ── DC Schemes: how many of the lab's open schemes already have a
    // challan ("3/4"). Hover shows the names of the pending schemes.
    const dcSchemesColumn = {
      dataField: "_dc_schemes",
      text: "DC Schemes",
      isDummyField: true,
      headerStyle: {
        width: "80px",
        textAlign: "center",
        position: "sticky",
        top: 0,
        zIndex: 4,
        background: "#fff",
      },
      style: (cell, row) => ({
        width: "80px",
        textAlign: "center",
        fontSize: "11px",
        background: this.getRowBackground(row),
      }),
      formatter: (cell, row) => {
        const s = this.getSchemeDcSummary(row.id);
        if (!s || !s.schemes) return "-";

        const done = s.schemes - s.pendingSchemes;

        return (
          <span
            title={
              s.pendingSchemes
                ? `Pending: ${s.pendingNames.join(", ")}`
                : "All schemes have a DC"
            }
            style={{
              fontWeight: 700,
              color: s.pendingSchemes ? "#c62828" : "#2e7d32",
            }}
          >
            {done}/{s.schemes}
          </span>
        );
      },
    };

    const dynamicColumns = (schemes || []).map(scheme => {
      const n = normalize(scheme);
      return {
        dataField: n,
        text: getAbbr(scheme),
        sort: false,
        headerStyle: {
          width: "28px",
          minWidth: "28px",
          maxWidth: "28px",
          writingMode: "vertical-rl",
          transform: "rotate(180deg)",
          textAlign: "center",
          fontSize: "10px",
          padding: "0",
          position: "sticky",
          top: 0,
          zIndex: 4,
          background: "#fff",
        },
        style: (cell, row) => ({
          width: "28px",
          minWidth: "28px",
          maxWidth: "28px",
          padding: "0",
          textAlign: "center",
          background: this.getRowBackground(row),
        }),
        formatter: (cell, row) => {
          // Only show a checkbox when this participant is in a round that
          // matches the active filters; checked when that round has been
          // dispatched.
          const entry = this._roundLookup?.[`${row.id}|${n}`];
          if (!entry) return null;
          const isDelivered = entry.allGot === true;
          return (
            <div
              style={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <input
                type="checkbox"
                checked={isDelivered}
                readOnly
                style={{
                  cursor: "not-allowed",
                  accentColor: "#2e7d32",
                  pointerEvents: "none",
                }}
              />
            </div>
          );
        },
      };
    });

    const extraColumns = [
      {
        dataField: "dispatch_date",
        text: "Date of Dispatch",
        formatter: cell => formatDate(cell),
        headerStyle: {
          width: "95px",
          textAlign: "center",
          position: "sticky",
          top: 0,
          zIndex: 4,
          background: "#fff",
        },
        style: (cell, row) => ({
          fontSize: "11px",
          textAlign: "center",
          background: this.getRowBackground(row),
        }),
      },
      {
        dataField: "tracking_id",
        text: "Tracking ID",
        headerStyle: {
          width: "110px",
          textAlign: "center",
          position: "sticky",
          top: 0,
          zIndex: 4,
          background: "#fff",
        },
        style: (cell, row) => ({
          fontSize: "11px",
          textAlign: "center",
          background: this.getRowBackground(row),
        }),
        formatter: (cell, row) =>
          row.tracking_id && row.tracking_id !== "-" ? row.tracking_id : "-",
      },
    ];

    const actionColumn = [
      {
        dataField: "_action",
        text: "Action",
        isDummyField: true,
        headerStyle: {
          width: "120px",
          textAlign: "center",
          position: "sticky",
          top: 0,
          zIndex: 4,
          background: "#fff",
        },
        style: (cell, row) => ({
          width: "120px",
          textAlign: "center",
          background: this.getRowBackground(row),
        }),
        formatter: (cell, row) => (
          <div className="d-flex justify-content-center gap-1">
            {/* Add */}
            <i
              className="mdi mdi-plus-circle-outline text-success"
              style={{ cursor: "pointer", fontSize: "16px" }}
              onClick={() => this.handleAddClick(row)}
            />

            {/* Edit */}
            {this.hasAnyDispatchedRound(row) && (
              <i
                className="mdi mdi-pencil text-primary"
                style={{ cursor: "pointer", fontSize: "16px" }}
                onClick={() => this.handleEditClick(row)}
              />
            )}

            {/* Delivery — opens the challan in a new TAB */}
            <i
              className="mdi mdi-truck-delivery-outline text-info"
              style={{ cursor: "pointer", fontSize: "16px" }}
              title="Open Delivery Challan in a new tab"
              onClick={() => this.openDeliveryChallan(row, true)}
            />

            {/* Repeat DC — only once a DC exists for this participant.
                Opens the challan page in repeat mode (existing DCs in one
                table + "New DC" on a new date) in a new TAB. */}
            {toBool(row.dc_created) && (
              <i
                className="mdi mdi-content-copy text-warning"
                style={{ cursor: "pointer", fontSize: "16px" }}
                title="Repeat DC (open in a new tab)"
                onClick={() => this.openDeliveryChallan(row, true, true)}
              />
            )}
          </div>
        ),
      },
    ];

    return [
      ...fixedColumns,
      ...dcColumns,
      dcSchemesColumn,
      ...dynamicColumns,
      ...extraColumns,
      ...actionColumn,
    ];
  }

  // ─── Stats ────────────────────────────────────────────────────────────────

  // ✅ Reuses getFilteredData (without the status filter) so the stat cards
  // and the table can never disagree about which filters apply.
  getDispatchStats = () => {
    const filteredParticipants = this.getFilteredData(true);
    const total = filteredParticipants.length;

    if (!total) {
      return {
        total: 0,
        dispatched: 0,
        pending: 0,
        partiallyDispatched: 0,
        dispatchedPct: 0,
        pendingPct: 0,
        partiallyDispatchedPct: 0,
        dcCreated: 0,
        dcPartial: 0,
        dcPending: 0,
        dcCreatedPct: 0,
        dcPartialPct: 0,
        dcPendingPct: 0,
        schemeTotal: 0,
        schemePending: 0,
        schemeCreated: 0,
        schemePendingPct: 0,
      };
    }

    const pct = n => Math.round((n / total) * 100);

    const dispatched = filteredParticipants.filter(row =>
      this.isFullyDispatched(row)
    ).length;

    const partiallyDispatched = filteredParticipants.filter(row =>
      this.isPartiallyDispatched(row)
    ).length;

    const pending = filteredParticipants.filter(
      row => !this.isFullyDispatched(row)
    ).length;

    // ── Delivery Challan: labs AND schemes ──
    let dcCreated = 0; // DC created for ALL schemes
    let dcPartial = 0; // DC created, but some schemes still pending
    let dcPending = 0; // no DC at all
    let schemeTotal = 0;
    let schemePending = 0;

    filteredParticipants.forEach(row => {
      const summary = this.getSchemeDcSummary(row.id);

      if (summary) {
        schemeTotal += summary.schemes;
        schemePending += summary.pendingSchemes;
      }

      if (!toBool(row.dc_created)) dcPending += 1;
      else if (summary && summary.pendingSchemes > 0) dcPartial += 1;
      else dcCreated += 1;
    });

    const schemeCreated = schemeTotal - schemePending;

    return {
      total,
      dispatched,
      pending,
      partiallyDispatched,
      dispatchedPct: pct(dispatched),
      pendingPct: pct(pending),
      partiallyDispatchedPct: pct(partiallyDispatched),
      dcCreated,
      dcPartial,
      dcPending,
      dcCreatedPct: pct(dcCreated),
      dcPartialPct: pct(dcPartial),
      dcPendingPct: pct(dcPending),
      schemeTotal,
      schemePending,
      schemeCreated,
      schemePendingPct: schemeTotal
        ? Math.round((schemePending / schemeTotal) * 100)
        : 0,
    };
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  render() {
    const { structuredData, modalOpen, selectedRowData, formData } = this.state;
    const { SampleReport } = this.props;
    // Rebuilt every render so the scheme checkboxes follow the filters.
    this._roundLookup = this.buildRoundLookup();
    // 🚚 Per lab + scheme: how many open rounds still have no challan.
    // Must be set BEFORE generateColumns / getRowBackground run.
    this._schemeDcLookup = this.buildSchemeDcLookup();
    const columns = this.generateColumns();
    const { focusedParticipantId } = this.state;
    const allFilteredData = this.getFilteredData();
    // A lab picked from the bell narrows the TABLE to just that lab
    // (stat cards and the PDF still use the normal filters).
    const filteredData = focusedParticipantId
      ? allFilteredData.filter(r => r.id === focusedParticipantId)
      : allFilteredData;
    const focusedLab = focusedParticipantId
      ? structuredData.find(r => r.id === focusedParticipantId)
      : null;
    const pageOptions = {
      sizePerPage: 100,
      totalSize: filteredData.length,
      custom: true,
    };
    const defaultSorted = [{ dataField: "district", order: "asc" }];

    // Cities offered in the City filter, narrowed by the currently
    // selected District(s) — see getCityOptionsSource.
    const cityOptionsSource = this.getCityOptionsSource();

    // 🔔 Labs where DC is not created, partly created, or not printed.
    const allPendingDc = this.getPendingDcParticipants();
    const bellSearchTerm = normalize(this.state.bellSearch);
    const pendingDc = bellSearchTerm
      ? allPendingDc.filter(p =>
          normalize(`${p.lab_name} ${p.city} ${p.district}`).includes(
            bellSearchTerm
          )
        )
      : allPendingDc;
    const notCreatedCount = allPendingDc.filter(
      p => !toBool(p.dc_created)
    ).length;
    const partialCount = allPendingDc.filter(p => {
      const s = this.getSchemeDcSummary(p.id);
      return toBool(p.dc_created) && s && s.pendingSchemes > 0;
    }).length;
    const notPrintedCount =
      allPendingDc.length - notCreatedCount - partialCount;
    const BELL_LIMIT = 100; // keep the dropdown fast with hundreds of labs

    const FilterSelect = ({ label, stateKey, onChange, children }) => (
      <Col lg="3" md="4" sm="6" className="mb-2">
        <h6 className="mb-2">{label}</h6>
        <select
          className={`form-select ${
            this.state[stateKey] !== "All" ? "border-primary" : ""
          }`}
          value={this.state[stateKey]}
          onChange={onChange}
        >
          {children}
        </select>
      </Col>
    );

    const show =
      this.state.dispatchTypeFilter === "New" ||
      this.state.dispatchTypeFilter === "Previous";

    return (
      <div className="page-content">
        <MetaTags>
          <title>Sample Dispatch Report | NEQAS</title>
        </MetaTags>
        <Container fluid>
          <h5>Sample Dispatch Details Report</h5>
          <div className="mb-3 ms-1 d-flex justify-content-between align-items-start">
            <div>
              <h6 className="fw-bold">Note:</h6>
              <ul className="mb-0">
                <li>
                  {" "}
                  Only <strong>Open Rounds</strong> are considered for dispatch
                  calculations.
                </li>
                <li>
                  {" "}
                  A participant is marked as <strong>Dispatched</strong> only
                  when all open rounds issued to that Participant have been
                  dispatched.
                </li>
                <li>The download Report is Pdf; based on Selected Filter.</li>
              </ul>
            </div>

            <div className="d-flex align-items-center gap-2">
              {/* 🔔 DC pending labs */}
              <Dropdown isOpen={this.state.bellOpen} toggle={this.toggleBell}>
                <DropdownToggle
                  color="light"
                  className="position-relative"
                  title="Labs with DC not created / schemes pending / not printed"
                  style={{ padding: "6px 12px" }}
                >
                  <i
                    className={`mdi ${
                      allPendingDc.length ? "mdi-bell-ring" : "mdi-bell-outline"
                    }`}
                    style={{
                      fontSize: "20px",
                      color: allPendingDc.length ? "#fb000090" : "#555",
                    }}
                  />
                  {allPendingDc.length > 0 && (
                    <span
                      style={{
                        position: "absolute",
                        top: "-6px",
                        right: "-6px",
                        background: "#dc3545",
                        color: "#fff",
                        borderRadius: "10px",
                        fontSize: "11px",
                        fontWeight: 700,
                        padding: "1px 6px",
                        minWidth: "18px",
                        textAlign: "center",
                      }}
                    >
                      {allPendingDc.length > 999 ? "999+" : allPendingDc.length}
                    </span>
                  )}
                </DropdownToggle>

                {/* NOTE: `right` is for reactstrap v8 (Bootstrap 4).
                    If you are on reactstrap v9, change it to `end`. */}
                <DropdownMenu
                  right
                  style={{
                    width: "380px",
                    maxHeight: "460px",
                    overflowY: "auto",
                  }}
                >
                  <div
                    className="px-3 py-2 border-bottom"
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <strong>DC Pending ({allPendingDc.length})</strong>
                    </div>
                    <div className="text-muted" style={{ fontSize: "11px" }}>
                      {notCreatedCount} DC not created · {partialCount} some
                      schemes pending · {notPrintedCount} created, not printed
                    </div>
                    {allPendingDc.length > 0 && (
                      <input
                        type="text"
                        className="form-control form-control-sm mt-2"
                        placeholder="Search lab, city or district..."
                        value={this.state.bellSearch}
                        onChange={e =>
                          this.setState({ bellSearch: e.target.value })
                        }
                      />
                    )}
                  </div>

                  {allPendingDc.length === 0 ? (
                    <div
                      className="text-muted text-center py-4"
                      style={{ fontSize: "13px" }}
                    >
                      <i
                        className="mdi mdi-check-circle-outline d-block"
                        style={{ fontSize: "28px" }}
                      />
                      All labs have DC created and printed
                    </div>
                  ) : pendingDc.length === 0 ? (
                    <div
                      className="text-muted text-center py-3"
                      style={{ fontSize: "12px" }}
                    >
                      No labs match your search
                    </div>
                  ) : (
                    <>
                      {pendingDc.slice(0, BELL_LIMIT).map(p => {
                        const created = toBool(p.dc_created);
                        const s = this.getSchemeDcSummary(p.id);
                        const partial = created && s && s.pendingSchemes > 0;
                        return (
                          <div
                            key={p.id}
                            className="px-3 py-2 border-bottom d-flex justify-content-between align-items-start"
                            style={{ gap: "8px" }}
                          >
                            <div style={{ fontSize: "12px", minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: "13px",
                                  cursor: "pointer",
                                  color: "#3b5de7",
                                }}
                                title="Show this lab in the table"
                                onClick={() => this.showParticipantInTable(p)}
                              >
                                {p.lab_name}
                              </div>
                              <div className="text-muted">
                                {p.city !== "-" ? p.city : ""}
                                {p.city !== "-" && p.district !== "-"
                                  ? ", "
                                  : ""}
                                {p.district !== "-" ? p.district : ""}
                              </div>
                              <span
                                style={{
                                  display: "inline-block",
                                  marginTop: "3px",
                                  fontSize: "10px",
                                  fontWeight: 600,
                                  padding: "1px 7px",
                                  borderRadius: "10px",
                                  background: partial
                                    ? "#fff3cd"
                                    : created
                                    ? "#fff3e0"
                                    : "#fde8ea",
                                  color: partial
                                    ? "#856404"
                                    : created
                                    ? "#e65100"
                                    : "#c62828",
                                }}
                              >
                                {partial
                                  ? `DC created, ${
                                      s.pendingSchemes
                                    } scheme(s) pending: ${s.pendingNames.join(
                                      ", "
                                    )}`
                                  : created
                                  ? "DC created, not printed"
                                  : "DC not created"}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      {pendingDc.length > BELL_LIMIT && (
                        <div
                          className="text-muted text-center py-2"
                          style={{ fontSize: "11px" }}
                        >
                          Showing first {BELL_LIMIT} of {pendingDc.length}. Use
                          search to find others.
                        </div>
                      )}
                    </>
                  )}
                </DropdownMenu>
              </Dropdown>

              <Button color="primary" onClick={this.downloadAllParticipantsPDF}>
                <i className="mdi mdi-download me-1"></i>Download Report
              </Button>
            </div>
          </div>
          {(() => {
            const {
              total,
              dispatched,
              pending,
              partiallyDispatched,
              dispatchedPct,
              pendingPct,
              partiallyDispatchedPct,
              dcCreated,
              dcPartial,
              dcPending,
              dcCreatedPct,
              dcPartialPct,
              dcPendingPct,
              schemeTotal,
              schemePending,
              schemeCreated,
              schemePendingPct,
            } = this.getDispatchStats();
            if (!total) return null;
            return (
              <Row className="mb-3 mx-1">
                <Col xs="12" sm="6" lg className="mb-2">
                  <div
                    style={{
                      background: "#f0f4ff",
                      border: "1px solid #d0d9f5",
                      borderRadius: "10px",
                      padding: "14px 18px",
                      display: "flex",
                      alignItems: "center",
                      gap: "14px",
                    }}
                  >
                    <i
                      className="mdi mdi-account-group"
                      style={{ fontSize: "30px", color: "#5b73e8" }}
                    />
                    <div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#888",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Total Participants
                      </div>
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: 700,
                          color: "#3a3a3a",
                          lineHeight: 1.2,
                        }}
                      >
                        {total}
                      </div>
                    </div>
                  </div>
                </Col>

                <Col xs="12" sm="6" lg className="mb-2">
                  <div
                    style={{
                      background: "#f0faf4",
                      border: "1px solid #b7e4c7",
                      borderRadius: "10px",
                      padding: "14px 18px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                        marginBottom: "10px",
                      }}
                    >
                      <i
                        className="mdi mdi-check-circle"
                        style={{ fontSize: "30px", color: "#28a745" }}
                      />
                      <div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#888",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Dispatched
                        </div>
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: 700,
                            color: "#28a745",
                            lineHeight: 1.2,
                          }}
                        >
                          {dispatched}{" "}
                          <span
                            style={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            ({dispatchedPct}%)
                          </span>
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        background: "#d4edda",
                        borderRadius: "4px",
                        height: "7px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${dispatchedPct}%`,
                          background: "#28a745",
                          height: "100%",
                          borderRadius: "4px",
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                  </div>
                </Col>

                <Col xs="12" sm="6" lg className="mb-2">
                  <div
                    style={{
                      background: "#f5f0fa",
                      border: "1px solid #d9c2f0",
                      borderRadius: "10px",
                      padding: "14px 18px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                        marginBottom: "10px",
                      }}
                    >
                      <i
                        className="mdi mdi-timer-sand"
                        style={{ fontSize: "30px", color: "#8e44ad" }}
                      />
                      <div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#888",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Partially Dispatched
                        </div>
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: 700,
                            color: "#8e44ad",
                            lineHeight: 1.2,
                          }}
                        >
                          {partiallyDispatched}{" "}
                          <span
                            style={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            ({partiallyDispatchedPct}%)
                          </span>
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        background: "#e6d6f2",
                        borderRadius: "4px",
                        height: "7px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${partiallyDispatchedPct}%`,
                          background: "#8e44ad",
                          height: "100%",
                          borderRadius: "4px",
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                  </div>
                </Col>

                <Col xs="12" sm="6" lg className="mb-2">
                  <div
                    style={{
                      background: "#fff8f0",
                      border: "1px solid #f5c6a0",
                      borderRadius: "10px",
                      padding: "14px 18px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                        marginBottom: "10px",
                      }}
                    >
                      <i
                        className="mdi mdi-clock-outline"
                        style={{ fontSize: "30px", color: "#fd7e14" }}
                      />
                      <div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#888",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Pending
                        </div>
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: 700,
                            color: "#fd7e14",
                            lineHeight: 1.2,
                          }}
                        >
                          {pending}{" "}
                          <span
                            style={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            ({pendingPct}%)
                          </span>
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        background: "#ffe5cc",
                        borderRadius: "4px",
                        height: "7px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${pendingPct}%`,
                          background: "#fd7e14",
                          height: "100%",
                          borderRadius: "4px",
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                  </div>
                </Col>

                {/* ── Delivery Challan: labs AND schemes ── */}
                <Col xs="12" sm="6" lg className="mb-2">
                  <div
                    style={{
                      background: "#f4f9ff",
                      border: "1px solid #cfe0f5",
                      borderRadius: "10px",
                      padding: "14px 18px",
                      height: "100%",
                    }}
                  >
                    <div
                      className="d-flex align-items-center"
                      style={{ gap: "12px", marginBottom: "8px" }}
                    >
                      <i
                        className="mdi mdi-truck-delivery"
                        style={{ fontSize: "30px", color: "#28a745" }}
                      />
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#888",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Delivery Challan
                      </div>
                    </div>

                    {[
                      { head: "Labs" },
                      {
                        label: "All schemes created",
                        value: dcCreated,
                        pct: dcCreatedPct,
                        color: "#28a745",
                      },
                      {
                        label: "Some schemes pending",
                        value: dcPartial,
                        pct: dcPartialPct,
                        color: "#b8860b",
                      },
                      {
                        label: "Not created",
                        value: dcPending,
                        pct: dcPendingPct,
                        color: "#fd7e14",
                      },
                      { head: "Schemes Sample" },
                      {
                        label: `Created (of ${schemeTotal})`,
                        value: schemeCreated,
                        pct: schemeTotal
                          ? Math.round((schemeCreated / schemeTotal) * 100)
                          : 0,
                        color: "#28a745",
                      },
                      {
                        label: "Pending",
                        value: schemePending,
                        pct: schemePendingPct,
                        color: "#fd7e14",
                      },
                    ].map((item, i) =>
                      item.head ? (
                        <div
                          key={item.head}
                          style={{
                            fontSize: "10px",
                            fontWeight: 700,
                            color: "#888",
                            textTransform: "uppercase",
                            marginTop: i ? "6px" : 0,
                          }}
                        >
                          {item.head}
                        </div>
                      ) : (
                        <div
                          key={item.label}
                          className="d-flex justify-content-between align-items-baseline"
                          style={{ fontSize: "13px", lineHeight: 1.7 }}
                        >
                          <span style={{ color: "#555" }}>{item.label}</span>
                          <span style={{ fontWeight: 700, color: item.color }}>
                            {item.value}{" "}
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              ({item.pct}%)
                            </span>
                          </span>
                        </div>
                      )
                    )}

                    {/* green = all schemes done, yellow = partial, rest = not created */}
                    <div
                      style={{
                        background: "#ffe5cc",
                        borderRadius: "4px",
                        height: "7px",
                        overflow: "hidden",
                        marginTop: "8px",
                        display: "flex",
                      }}
                    >
                      <div
                        style={{
                          width: `${dcCreatedPct}%`,
                          background: "#28a745",
                          height: "100%",
                          transition: "width 0.4s ease",
                        }}
                      />
                      <div
                        style={{
                          width: `${dcPartialPct}%`,
                          background: "#ffc107",
                          height: "100%",
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            );
          })()}

          <Row className="mb-3 align-items-end ms-3 flex-wrap">
            <Col lg="3" md="4" sm="6" className="mb-2">
              <h6 className="mb-2">Select Dispatch Type</h6>
              <select
                className="form-select"
                value={this.state.dispatchTypeFilter}
                onChange={this.handleDispatchTypeChange}
              >
                <option value="New">New Dispatches</option>
                <option value="Previous">Previous Dispatched</option>
              </select>
            </Col>

            {show && (
              <FilterSelect
                label="Filter by Scheme"
                stateKey="selectedSchemeFilter"
                onChange={this.handleSchemeFilterChange}
              >
                <option value="All">All Schemes</option>
                {this.state.schemes.map((s, i) => (
                  <option key={i} value={s}>
                    {s}
                  </option>
                ))}
              </FilterSelect>
            )}
            {this.state.selectedSchemeFilter !== "All" &&
              this.state.cyclesForSelectedScheme.length > 0 && (
                <FilterSelect
                  label="Filter by Cycle"
                  stateKey="selectedCycleFilter"
                  onChange={this.handleCycleFilterChange}
                >
                  <option value="All">All Cycles</option>
                  {this.state.cyclesForSelectedScheme.map((c, i) => (
                    <option key={i} value={c}>
                      {c}
                    </option>
                  ))}
                </FilterSelect>
              )}
            {this.state.selectedSchemeFilter !== "All" && (
              <FilterSelect
                label="Filter by Round"
                stateKey="selectedRoundFilter"
                onChange={this.handleRoundFilterChange}
              >
                <option value="All">All Rounds</option>
                {(this.state.roundsForSelectedScheme || [])
                  .filter(
                    r =>
                      this.state.selectedRoundPeriodFilter === "All" ||
                      normalize(r.round_period || "") ===
                        normalize(this.state.selectedRoundPeriodFilter)
                  )
                  .map((r, i) => (
                    <option key={i} value={r.rounds}>
                      {r.rounds}
                    </option>
                  ))}
              </FilterSelect>
            )}

            {/* ✅ Round Period filter (Previous / Current / Next) —
                only shown for New Dispatches, since round_period is
                only meaningful for currently-open rounds. */}
            {this.state.dispatchTypeFilter === "New" && (
              <FilterSelect
                label="Filter by Round Period"
                stateKey="selectedRoundPeriodFilter"
                onChange={e =>
                  this.setState({
                    selectedRoundPeriodFilter: e.target.value,
                    // the round list depends on the period, so reset it
                    selectedRoundFilter: "All",
                  })
                }
              >
                <option value="All">All Periods</option>
                <option value="Previous">Previous</option>
                <option value="Current">Current</option>
                <option value="Next">Next</option>
              </FilterSelect>
            )}

            {show && (
              <FilterSelect
                label="Filter by Status"
                stateKey="selectedStatusFilter"
                onChange={e =>
                  this.setState({ selectedStatusFilter: e.target.value })
                }
              >
                <option value="All">All</option>
                <option value="Pending">Pending</option>
                <option value="Dispatched">Dispatched</option>
                <option value="Partially Dispatched">
                  Partially Dispatched
                </option>
              </FilterSelect>
            )}

            {/* ── District filter: multi-select dropdown with checkboxes ── */}
            {/* Selecting a District narrows the City filter's options
                (see cityOptionsSource above) and resets City to "All". */}
            {show && (
              <Col lg="3" md="4" sm="6" className="mb-2">
                <h6 className="mb-2">Filter by District</h6>
                <Dropdown
                  isOpen={this.state.districtDropdownOpen}
                  toggle={this.toggleDistrictDropdown}
                >
                  <DropdownToggle
                    caret
                    className={`form-select text-start ${
                      !this.state.selectedDistrictFilter.includes("All")
                        ? "border-primary"
                        : ""
                    }`}
                    style={{ width: "100%" }}
                  >
                    {this.getDistrictFilterDisplayLabel()}
                  </DropdownToggle>
                  <DropdownMenu
                    style={{
                      maxHeight: "320px",
                      overflowY: "auto",
                      width: "100%",
                    }}
                  >
                    <div
                      className="px-3 py-1"
                      onClick={e => e.stopPropagation()}
                    >
                      {/* Search box */}
                      <input
                        type="text"
                        className="form-control form-control-sm mb-2"
                        placeholder="Search district..."
                        value={this.state.districtSearchTerm}
                        onChange={this.handleDistrictSearchChange}
                        onClick={e => e.stopPropagation()}
                        autoFocus
                      />

                      <div className="form-check mb-1">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="district-all"
                          checked={this.state.selectedDistrictFilter.includes(
                            "All"
                          )}
                          onChange={() =>
                            this.handleDistrictCheckboxChange("All")
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="district-all"
                        >
                          All Districts
                        </label>
                      </div>

                      <hr className="my-1" />

                      {[...new Set(structuredData.map(row => row.district))]
                        .filter(district => district && district !== "-")
                        .filter(district =>
                          normalize(district).includes(
                            normalize(this.state.districtSearchTerm)
                          )
                        )
                        .map((district, index) => (
                          <div className="form-check mb-1" key={index}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`district-${index}`}
                              checked={this.state.selectedDistrictFilter.includes(
                                district
                              )}
                              onChange={() =>
                                this.handleDistrictCheckboxChange(district)
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`district-${index}`}
                            >
                              {district}
                            </label>
                          </div>
                        ))}

                      {this.state.districtSearchTerm &&
                        [...new Set(structuredData.map(row => row.district))]
                          .filter(district => district && district !== "-")
                          .filter(district =>
                            normalize(district).includes(
                              normalize(this.state.districtSearchTerm)
                            )
                          ).length === 0 && (
                          <div
                            className="text-muted"
                            style={{ fontSize: "12px", padding: "4px 0" }}
                          >
                            No districts match &ldquo;
                            {this.state.districtSearchTerm}&rdquo;
                          </div>
                        )}
                    </div>
                  </DropdownMenu>
                </Dropdown>
              </Col>
            )}

            {/* ── City filter: multi-select dropdown with checkboxes ── */}
            {show && (
              <Col lg="3" md="4" sm="6" className="mb-2">
                <h6 className="mb-2">Filter by City</h6>
                <Dropdown
                  isOpen={this.state.cityDropdownOpen}
                  toggle={this.toggleCityDropdown}
                >
                  <DropdownToggle
                    caret
                    className={`form-select text-start ${
                      !this.state.selectedCityFilter.includes("All")
                        ? "border-primary"
                        : ""
                    }`}
                    style={{ width: "100%" }}
                  >
                    {this.getCityFilterDisplayLabel()}
                  </DropdownToggle>
                  <DropdownMenu
                    style={{
                      maxHeight: "320px",
                      overflowY: "auto",
                      width: "100%",
                    }}
                  >
                    <div
                      className="px-3 py-1"
                      onClick={e => e.stopPropagation()}
                    >
                      {/* Search box */}
                      <input
                        type="text"
                        className="form-control form-control-sm mb-2"
                        placeholder="Search city..."
                        value={this.state.citySearchTerm}
                        onChange={this.handleCitySearchChange}
                        onClick={e => e.stopPropagation()}
                        autoFocus
                      />

                      <div className="form-check mb-1">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="city-all"
                          checked={this.state.selectedCityFilter.includes(
                            "All"
                          )}
                          onChange={() => this.handleCityCheckboxChange("All")}
                        />
                        <label className="form-check-label" htmlFor="city-all">
                          All Cities
                        </label>
                      </div>

                      <div className="form-check mb-1">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="city-gcc"
                          checked={this.state.selectedCityFilter.includes(
                            GCC_FILTER_VALUE
                          )}
                          onChange={() =>
                            this.handleCityCheckboxChange(GCC_FILTER_VALUE)
                          }
                        />
                        <label className="form-check-label" htmlFor="city-gcc">
                          {GCC_FILTER_LABEL}
                        </label>
                      </div>

                      <hr className="my-1" />

                      {/* Uses cityOptionsSource instead of structuredData
                          directly, so this list narrows down to whichever
                          District(s) are currently selected above. */}
                      {[...new Set(cityOptionsSource.map(row => row.city))]
                        .filter(city => city && city !== "-")
                        .filter(city =>
                          normalize(city).includes(
                            normalize(this.state.citySearchTerm)
                          )
                        )
                        .map((city, index) => (
                          <div className="form-check mb-1" key={index}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`city-${index}`}
                              checked={this.state.selectedCityFilter.includes(
                                city
                              )}
                              onChange={() =>
                                this.handleCityCheckboxChange(city)
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`city-${index}`}
                            >
                              {city}
                            </label>
                          </div>
                        ))}

                      {this.state.citySearchTerm &&
                        [...new Set(cityOptionsSource.map(row => row.city))]
                          .filter(city => city && city !== "-")
                          .filter(city =>
                            normalize(city).includes(
                              normalize(this.state.citySearchTerm)
                            )
                          ).length === 0 && (
                          <div
                            className="text-muted"
                            style={{ fontSize: "12px", padding: "4px 0" }}
                          >
                            No cities match &ldquo;{this.state.citySearchTerm}
                            &rdquo;
                          </div>
                        )}
                    </div>
                  </DropdownMenu>
                </Dropdown>
              </Col>
            )}

            {/* ── Lab Type filter: multi-select dropdown with checkboxes ── */}
            {show && (
              <Col lg="3" md="4" sm="6" className="mb-2">
                <h6 className="mb-2">Filter by Lab Type</h6>
                <Dropdown
                  isOpen={this.state.typeDropdownOpen}
                  toggle={this.toggleTypeDropdown}
                >
                  <DropdownToggle
                    caret
                    className={`form-select text-start ${
                      !this.state.selectedTypeFilter.includes("All")
                        ? "border-primary"
                        : ""
                    }`}
                    style={{ width: "100%" }}
                  >
                    {this.getTypeFilterDisplayLabel()}
                  </DropdownToggle>
                  <DropdownMenu
                    style={{
                      maxHeight: "260px",
                      overflowY: "auto",
                      width: "100%",
                    }}
                  >
                    <div
                      className="px-3 py-1"
                      onClick={e => e.stopPropagation()}
                    >
                      <div className="form-check mb-1">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="type-all"
                          checked={this.state.selectedTypeFilter.includes(
                            "All"
                          )}
                          onChange={() => this.handleTypeCheckboxChange("All")}
                        />
                        <label className="form-check-label" htmlFor="type-all">
                          All Types
                        </label>
                      </div>

                      <div className="form-check mb-1">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="type-gcc"
                          checked={this.state.selectedTypeFilter.includes(
                            GCC_FILTER_VALUE
                          )}
                          onChange={() =>
                            this.handleTypeCheckboxChange(GCC_FILTER_VALUE)
                          }
                        />
                        <label className="form-check-label" htmlFor="type-gcc">
                          {GCC_FILTER_LABEL}
                        </label>
                      </div>

                      <hr className="my-1" />

                      {[
                        ...new Set(
                          structuredData
                            .map(row => row.type)
                            .filter(type => type && type !== "-")
                        ),
                      ].map((type, index) => (
                        <div className="form-check mb-1" key={index}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`type-${index}`}
                            checked={this.state.selectedTypeFilter.includes(
                              type
                            )}
                            onChange={() => this.handleTypeCheckboxChange(type)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`type-${index}`}
                          >
                            {type}
                          </label>
                        </div>
                      ))}
                    </div>
                  </DropdownMenu>
                </Dropdown>
              </Col>
            )}
          </Row>

          <Row className="justify-content-center">
            <Col lg="11">
              <div ref={this.tableTopRef} />
              {focusedParticipantId && (
                <div
                  className="d-flex justify-content-between align-items-center mb-2 px-3 py-2"
                  style={{
                    background: "#e8f0fe",
                    border: "1px solid #b6ccf5",
                    borderRadius: "8px",
                    fontSize: "13px",
                  }}
                >
                  <span>
                    <i className="mdi mdi-filter me-1" />
                    Showing only:{" "}
                    <strong>{focusedLab?.lab_name || "selected lab"}</strong>
                  </span>
                  <Button
                    color="link"
                    size="sm"
                    className="p-0"
                    onClick={this.clearFocusedParticipant}
                  >
                    Show all labs
                  </Button>
                </div>
              )}
              <Card>
                <CardBody>
                  <PaginationProvider
                    pagination={paginationFactory(pageOptions)}
                  >
                    {({ paginationProps, paginationTableProps }) => (
                      <ToolkitProvider
                        keyField="id"
                        data={filteredData}
                        columns={columns}
                        search
                      >
                        {toolkitProps => (
                          <div>
                            <div
                              className="table-responsive"
                              style={{ maxHeight: "85vh", overflowY: "auto" }}
                            >
                              <BootstrapTable
                                bootstrap4
                                bordered
                                tableStyle={{
                                  tableLayout: "fixed",
                                  width: "100%",
                                }}
                                headerWrapperClasses="table-header-sky-blue"
                                {...toolkitProps.baseProps}
                                {...paginationTableProps}
                                // NOTE: defaultSorted and rowStyle are spread
                                // in AFTER toolkitProps.baseProps /
                                // paginationTableProps on purpose — those two
                                // objects can carry their own (undefined)
                                // values for these same prop names, and since
                                // JSX spreads apply in order, whichever comes
                                // last wins. Putting them first was silently
                                // wiping out the row background coloring.
                                defaultSorted={defaultSorted}
                                rowStyle={row => ({
                                  backgroundColor: this.getRowBackground(row),
                                })}
                                filter={filterFactory()}
                                noDataIndication={() => (
                                  <div
                                    style={{
                                      textAlign: "center",
                                      padding: "40px",
                                      color: "#888",
                                    }}
                                  >
                                    <i
                                      className="mdi mdi-filter-off"
                                      style={{
                                        fontSize: "32px",
                                        display: "block",
                                        marginBottom: "8px",
                                      }}
                                    />
                                    Loading.
                                  </div>
                                )}
                              />

                              <style>
                                {`
    /* Keep row colors permanent - disable Bootstrap hover override */
    .table tbody tr:hover {
      background-color: transparent !important;
    }

    .table tbody tr:hover > td {
      background-color: inherit !important;
    }

    /* Make each cell keep the background assigned by React */
    .table tbody td {
      transition: none !important;
    }
  `}
                              </style>
                            </div>
                            <PaginationListStandalone {...paginationProps} />
                          </div>
                        )}
                      </ToolkitProvider>
                    )}
                  </PaginationProvider>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Dispatch Modal */}
          <Modal isOpen={modalOpen} toggle={() => this.toggleModal()}>
            <ModalHeader toggle={() => this.toggleModal()}>
              {this.state.modalMode === "add"
                ? "Add New Dispatch"
                : "Edit Dispatch"}
            </ModalHeader>
            <ModalBody>
              {selectedRowData ? (
                <>
                  <div className="mb-3">
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    >
                      {selectedRowData.lab_name}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#555",
                        marginTop: "2px",
                      }}
                    >
                      {selectedRowData.city || "-"},{" "}
                      {selectedRowData.district || "-"}
                    </div>
                  </div>

                  {this.state.modalMode === "add" &&
                    (this.state.modalSchemes || []).length > 0 && (
                      <div className="mb-3">
                        <strong>Schemes & Open Rounds:</strong>
                        <div
                          className="mt-2"
                          style={{
                            background: "#eef3fb",
                            border: "1px solid #cfe0f5",
                            borderRadius: "8px",
                            padding: "10px 12px",
                          }}
                        >
                          <table
                            className="table table-sm mb-0"
                            style={{ background: "transparent" }}
                          >
                            <tbody>
                              {(this.state.modalSchemes || []).map(
                                (scheme, idx) => {
                                  const schemeObj = SampleReport?.find(
                                    s =>
                                      s.name?.trim().toLowerCase() ===
                                      scheme?.trim().toLowerCase()
                                  );

                                  const allRounds =
                                    schemeObj?.round_details || [];
                                  const dispatchedRounds = [];
                                  const openRounds = [];

                                  // ✅ Only rounds open_for "Both"/"Customer
                                  // Service" are eligible for dispatch here.
                                  const openAllRounds = allRounds.filter(
                                    round => isRoundOpenForDispatch(round)
                                  );

                                  openAllRounds.forEach(round => {
                                    const dispatchedReport = (
                                      this.state.selectedRowData
                                        ?.submitted_reports || []
                                    ).find(
                                      r =>
                                        r?.scheme_name?.trim().toLowerCase() ===
                                          scheme?.trim().toLowerCase() &&
                                        r?.round_name?.toString().trim() ===
                                          round.rounds?.toString().trim() &&
                                        !!r?.tracking_id
                                    );

                                    if (dispatchedReport) {
                                      dispatchedRounds.push({
                                        round,
                                        dispatchedReport,
                                      });
                                    } else {
                                      openRounds.push(round);
                                    }
                                  });

                                  return (
                                    <tr key={scheme + idx}>
                                      <td
                                        style={{
                                          fontWeight: 600,
                                          verticalAlign: "top",
                                          width: "35%",
                                        }}
                                      >
                                        {scheme}
                                      </td>
                                      <td>
                                        {openRounds.length > 0 ? (
                                          <div
                                            className="d-flex flex-wrap"
                                            style={{ gap: "12px" }}
                                          >
                                            {openRounds.map(round => {
                                              const checkKey = `checked_${scheme}_${round.rounds}`;
                                              return (
                                                <label
                                                  key={round.rounds}
                                                  className="d-flex align-items-center mb-0"
                                                  style={{
                                                    gap: "6px",
                                                    cursor: "pointer",
                                                  }}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    checked={
                                                      !!this.state
                                                        .selectedRowData?.[
                                                        checkKey
                                                      ]
                                                    }
                                                    onChange={e => {
                                                      this.setState(prev => ({
                                                        selectedRowData: {
                                                          ...prev.selectedRowData,
                                                          [checkKey]:
                                                            e.target.checked,
                                                        },
                                                      }));
                                                    }}
                                                  />
                                                  Round {round.rounds}
                                                </label>
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <span
                                            style={{
                                              color: "#888",
                                              fontSize: "12px",
                                            }}
                                          >
                                            No open rounds
                                          </span>
                                        )}
                                        {dispatchedRounds.length > 0 && (
                                          <div
                                            style={{
                                              fontSize: "11px",
                                              color: "green",
                                              marginTop: "4px",
                                            }}
                                          >
                                            Already Dispatched:{" "}
                                            {dispatchedRounds
                                              .map(
                                                ({ round }) =>
                                                  `Round ${round.rounds}`
                                              )
                                              .join(", ")}
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                }
                              )}
                            </tbody>
                          </table>
                        </div>
                        {this.state.formErrors?.roundSelection && (
                          <small className="text-danger d-block mt-1">
                            {this.state.formErrors.roundSelection}
                          </small>
                        )}
                      </div>
                    )}

                  {this.state.modalMode === "update" &&
                    (() => {
                      const grouped = {};
                      (selectedRowData.submitted_reports || []).forEach(r => {
                        if (!r || typeof r !== "object") return;
                        if (!r.tracking_id) return;

                        const schemeObj = SampleReport?.find(
                          s =>
                            s.name?.trim().toLowerCase() ===
                            r.scheme_name?.trim().toLowerCase()
                        );
                        const roundObj = (schemeObj?.round_details || []).find(
                          rd =>
                            rd.rounds?.toString().trim() ===
                            r.round_name?.toString().trim()
                        );
                        // ✅ same open_for restriction here too.
                        const isOpen = isRoundOpenForDispatch(roundObj);
                        if (!isOpen) return;

                        if (!grouped[r.scheme_name])
                          grouped[r.scheme_name] = [];
                        grouped[r.scheme_name].push(r);
                      });

                      if (!Object.keys(grouped).length) return null;

                      return (
                        <div className="mb-3">
                          <strong>Dispatched Samples:</strong>
                          <div
                            className="mt-2"
                            style={{
                              background: "#eef3fb",
                              border: "1px solid #cfe0f5",
                              borderRadius: "8px",
                              padding: "10px 12px",
                            }}
                          >
                            <table
                              className="table table-sm mb-0"
                              style={{ background: "transparent" }}
                            >
                              <tbody>
                                {Object.keys(grouped).map((schemeName, idx) => (
                                  <tr key={schemeName + idx}>
                                    <td
                                      style={{
                                        fontWeight: 600,
                                        verticalAlign: "top",
                                        width: "35%",
                                      }}
                                    >
                                      {schemeName}
                                    </td>
                                    <td>
                                      <div
                                        className="d-flex flex-wrap"
                                        style={{ gap: "12px" }}
                                      >
                                        {grouped[schemeName].map(
                                          (report, ri) => {
                                            const checkKey = `checked_${schemeName}_${report.round_name}`;
                                            const isChecked =
                                              !!selectedRowData[checkKey];
                                            return (
                                              <label
                                                key={ri}
                                                className="d-flex align-items-center mb-0"
                                                style={{
                                                  gap: "6px",
                                                  cursor: "pointer",
                                                }}
                                              >
                                                <input
                                                  type="checkbox"
                                                  checked={isChecked}
                                                  onChange={e => {
                                                    this.setState(prev => ({
                                                      selectedRowData: {
                                                        ...prev.selectedRowData,
                                                        [checkKey]:
                                                          e.target.checked,
                                                      },
                                                    }));
                                                  }}
                                                />
                                                Round {report.round_name || "—"}
                                              </label>
                                            );
                                          }
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {this.state.formErrors?.roundSelection && (
                            <small className="text-danger d-block mt-1">
                              {this.state.formErrors.roundSelection}
                            </small>
                          )}
                        </div>
                      );
                    })()}

                  {(() => {
                    if (this.state.modalMode === "add") {
                      const { modalSchemes, selectedRowData } = this.state;
                      const hasAnyOpenRound = (modalSchemes || []).some(
                        scheme => {
                          const schemeObj = SampleReport?.find(
                            s =>
                              s.name?.trim().toLowerCase() ===
                              scheme?.trim().toLowerCase()
                          );
                          // ✅ open_for restriction applied here too.
                          const openRounds = (
                            schemeObj?.round_details || []
                          ).filter(r => isRoundOpenForDispatch(r));
                          return openRounds.some(round => {
                            const alreadyDispatched = (
                              selectedRowData?.submitted_reports || []
                            ).some(
                              r =>
                                r?.scheme_name?.trim().toLowerCase() ===
                                  scheme?.trim().toLowerCase() &&
                                r?.round_name?.toString().trim() ===
                                  round.rounds?.toString().trim() &&
                                !!r?.tracking_id
                            );
                            return !alreadyDispatched;
                          });
                        }
                      );
                      if (!hasAnyOpenRound) return null;
                    }

                    return [
                      {
                        label: "Date of Dispatch",
                        name: "dispatchDate",
                        type: "date",
                      },
                      {
                        label: "Tracking ID",
                        name: "trackingId",
                        type: "text",
                      },
                      {
                        label: "Comments",
                        name: "comments",
                        type: "textarea",
                      },
                    ].map(({ label, name, type }) => (
                      <div className="mb-3" key={name}>
                        <label>{label}</label>
                        {type === "textarea" ? (
                          <textarea
                            name={name}
                            className="form-control"
                            rows="3"
                            placeholder="Add any notes about this dispatch"
                            value={formData[name]}
                            onChange={this.handleFormChange}
                          />
                        ) : (
                          <input
                            type={type}
                            name={name}
                            className="form-control"
                            value={formData[name]}
                            onChange={this.handleFormChange}
                          />
                        )}
                        {this.state.formErrors?.[name] && (
                          <small className="text-danger">
                            {this.state.formErrors[name]}
                          </small>
                        )}
                      </div>
                    ));
                  })()}
                </>
              ) : (
                <p>No data selected.</p>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="success" onClick={this.handleSave}>
                {this.state.modalMode === "add" ? "Save" : "Update"}
              </Button>
              <Button color="secondary" onClick={() => this.toggleModal()}>
                Cancel
              </Button>
            </ModalFooter>
          </Modal>

          {/* Download Confirmation Modal */}
          <Modal
            isOpen={this.state.showDownloadConfirm}
            toggle={() => this.setState({ showDownloadConfirm: false })}
            size="md"
            centered
          >
            <ModalHeader
              toggle={() => this.setState({ showDownloadConfirm: false })}
            >
              Confirm Download
            </ModalHeader>
            <ModalBody>
              <p>
                You are about to download{" "}
                <strong>{this.getFilteredData().length} participants</strong>{" "}
                with the following filters applied:
              </p>
              <ul style={{ fontSize: "13px", paddingLeft: "16px" }}>
                <li>
                  Dispatch Type:{" "}
                  <strong>{this.state.dispatchTypeFilter}</strong>
                </li>
                {this.state.selectedSchemeFilter !== "All" && (
                  <li>
                    Scheme: <strong>{this.state.selectedSchemeFilter}</strong>
                  </li>
                )}
                {this.state.selectedCycleFilter !== "All" && (
                  <li>
                    Cycle: <strong>{this.state.selectedCycleFilter}</strong>
                  </li>
                )}
                {this.state.selectedRoundFilter !== "All" && (
                  <li>
                    Round: <strong>{this.state.selectedRoundFilter}</strong>
                  </li>
                )}
                {this.state.selectedRoundPeriodFilter !== "All" && (
                  <li>
                    Round Period:{" "}
                    <strong>{this.state.selectedRoundPeriodFilter}</strong>
                  </li>
                )}
                {!this.state.selectedDistrictFilter.includes("All") && (
                  <li>
                    District:{" "}
                    <strong>{this.getDistrictFilterDisplayLabel()}</strong>
                  </li>
                )}
                {!this.state.selectedCityFilter.includes("All") && (
                  <li>
                    City: <strong>{this.getCityFilterDisplayLabel()}</strong>
                  </li>
                )}
                {!this.state.selectedTypeFilter.includes("All") && (
                  <li>
                    Lab Type:{" "}
                    <strong>{this.getTypeFilterDisplayLabel()}</strong>
                  </li>
                )}
                {this.state.selectedStatusFilter !== "All" && (
                  <li>
                    Status: <strong>{this.state.selectedStatusFilter}</strong>
                  </li>
                )}
              </ul>
            </ModalBody>
            <ModalFooter>
              <Button color="primary" onClick={this.proceedDownloadPDF}>
                <i className="mdi mdi-download me-1"></i>Download
              </Button>
              <Button
                color="secondary"
                onClick={() => this.setState({ showDownloadConfirm: false })}
              >
                Cancel
              </Button>
            </ModalFooter>
          </Modal>

          {/* Toast */}
          {this.state.toastMessage && (
            <div
              style={{
                position: "fixed",
                bottom: "24px",
                right: "24px",
                background: "#28a745",
                color: "#fff",
                padding: "12px 20px",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                zIndex: 9999,
                fontSize: "14px",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {this.state.toastMessage}
            </div>
          )}
        </Container>
      </div>
    );
  }
}

SchemeDetailsReport.propTypes = {
  SampleReport: PropTypes.array,
  onGetSampleReport: PropTypes.func,
  onPostSampleReport: PropTypes.func,
  onUpdateSampleReport: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    createHref: PropTypes.func,
  }).isRequired,

  match: PropTypes.shape({
    params: PropTypes.shape({
      organization_name: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

const mapStateToProps = state => ({
  SampleReport: state.ListUnit.SampleReport,
});
const mapDispatchToProps = dispatch => ({
  onGetSampleReport: id => dispatch(sampleReport(id)),
  onPostSampleReport: data => dispatch(postSampleReport(data)),
  onUpdateSampleReport: data => dispatch(updateSampleReport(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(SchemeDetailsReport));
